/**
 * ExecutionTestPopup.tsx
 * Popup for a single Playwright test result — mirrors TestCasePopup look and feel.
 * Uses tc-popup-* and rrw-* CSS classes from RequirementTestWorkspace.css.
 * Tabs: Summary · Steps · Evidence · Execution Log · Timing
 */
import React, { useMemo, useState, useEffect, useRef } from 'react';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import Form from 'react-bootstrap/Form';
import apiClient, { API_ENDPOINTS } from '../../api/config';
import { TEST_EXECUTION_DISABLED_MESSAGE } from '../../constants/featureMessages';
import { useActiveFeatureFlags } from '../../hooks/useActiveFeatureFlags';
import ProjectChatPanel from '../chat/ProjectChatPanel';
import '../requirements/RequirementTestWorkspace.css';
import './ExecutionsTab.css';

/* ─────────────────────────────────────────────
   Exported types (consumed by ExecutionsTab)
───────────────────────────────────────────── */
export interface StepResult {
  step_id?: string | number;
  step_index?: number;
  action?: string;
  expected?: string;
  success?: boolean;
  message?: string;
  error?: string | null;
  screenshot_path?: string | null;
  duration_ms?: number | null;
  not_executed?: boolean;
  is_assertion?: boolean;
  assertion_details?: {
    assertion_type?: string;
    expected?: string;
    actual?: string;
    current_url?: string;
    page_snapshot?: string;
    dom_snapshot?: string;
  };
}

export interface EvidenceFile {
  name: string;
  kind: string;
  relative_path: string;
  download_url?: string;
}

export interface ExecutionTestResult {
  test_case_key?: string;
  execution_key?: string;
  evidence_key?: string;
  execution_output_key?: string;
  synthetic_data?: boolean;
  variant_id?: string;
  variant_index?: number;
  version_number?: number | null;
  dataset_id?: string;
  dataset_name?: string;
  data?: Record<string, unknown>;
  row?: Record<string, unknown>;
  variants?: ExecutionTestResult[];
  variant_count?: number;
  passed_variants?: number;
  failed_variants?: number;
  title?: string;
  name?: string;
  pass?: boolean;
  status?: string;
  duration_ms?: number | null;
  error?: string | null;
  steps?: StepResult[] | string[];
  evidence?: {
    primary_screenshot_url?: string | null;
    files?: EvidenceFile[];
  } | null;
  start_time?: string | null;
  end_time?: string | null;
  run_id?: string | null;
  runner_blob_prefix?: string | null;
  failure_classification?: string | null;
  result_history?: Array<{
    id?: string;
    kind?: string;
    label?: string;
    created_at?: string;
    result?: ExecutionTestResult;
    run_id?: string | null;
    runner_blob_prefix?: string | null;
    evidence_key?: string | null;
    execution_key?: string | null;
    variant_id?: string | null;
    actions?: Record<string, PwActionStep>;
  }>;
}

export interface RunMeta {
  run_name?: string | null;
  base_url?: string | null;
  created_at?: string | null;
  status?: string;
  total_tests?: number;
  passed?: number;
  failed?: number;
  pass_rate?: number;
  /** Passed from ExecutionsTab so the popup can fetch per-test evidence */
  project_id?: number;
  job_id?: number;
  run_id?: string | null;
  /** Required for the Actions tab (resolved Playwright actions cache) */
  artifact_id?: number | null;
}

export interface ActionUpdateSummary {
  step_key: string;
  step_number: number;
  version_number: number;
  previous_type?: string;
  current_type?: string;
  label: string;
}

interface Props {
  test: ExecutionTestResult;
  runMeta: RunMeta;
  onClose: () => void;
  /** Called after a successful Save & Re-run kicks off a new playwright job. Passes the rerun job ID so the parent can poll it. */
  onRunStarted?: (rerunJobId?: number, rerunKey?: string) => void;
  /** Called with the parent or variant-scoped test key after a rerun is successfully queued. */
  onTestRerun?: (testKey: string) => void;
  /** Called when automated script edits create card-level update summaries. */
  onActionsUpdated?: (testKey: string, summaries: ActionUpdateSummary[], allSummaries?: Record<string, ActionUpdateSummary[]>) => void;
}

type PopupTab = 'summary' | 'data' | 'steps' | 'tc_steps' | 'actions' | 'evidence' | 'execution' | 'chat' | 'timing';

/** Action step format produced by the Playwright planner (resolved_actions.json) */
interface PwActionStep {
  type?: string;
  url?: string;
  wait_until?: string;
  locator_strategy?: string;
  locator_value?: string;
  value?: string;
  raw_action?: string;
  raw_expected?: string;
  [key: string]: unknown;
}

interface ResolvedActionsResponse {
  actions?: Record<string, Record<string, PwActionStep>>;
  test_case_steps?: Record<string, Record<string, PwActionStep>>;
  action_snapshots_by_run_id?: Record<string, Record<string, Record<string, PwActionStep>>>;
  edited_test_keys?: string[];
  rerun_test_keys?: string[];
  action_update_summaries?: Record<string, ActionUpdateSummary[]>;
  action_history?: Record<string, Array<{
    id?: string;
    kind?: string;
    label?: string;
    created_at?: string;
    actions?: Record<string, PwActionStep>;
  }>>;
}

type HistoryEntry = {
  id: string;
  label: string;
  kind: 'current' | 'result' | 'actions';
  created_at?: string;
  version_number?: number;
  run_id?: string | null;
  evidence_key?: string | null;
  execution_key?: string | null;
  variant_id?: string | null;
  result?: ExecutionTestResult;
  actions?: Record<string, PwActionStep>;
  /** Variants that actually executed in this version (see scopeVariantsToVersion). */
  variants?: ExecutionTestResult[];
};

/**
 * Variants that actually ran in a given version.
 *
 * Every parent snapshot stores the complete variant list as it stood at that
 * moment, so a version created by a single-variant re-run still carries all
 * variants. The untouched ones are carry-overs from earlier versions — they are
 * recognised by having no run id newer than the ones already seen.
 */
function scopeVariantsToVersion(
  allVariants: ExecutionTestResult[],
  previousRunIds: Set<string>,
): ExecutionTestResult[] {
  if (previousRunIds.size === 0 || allVariants.length <= 1) return allVariants;
  const executed = allVariants.filter(variant => {
    const runId = String(variant.run_id || '').trim();
    return !!runId && !previousRunIds.has(runId);
  });
  return executed.length > 0 ? executed : allVariants;
}

/** Collect every run id referenced by a version snapshot. */
function collectVersionRunIds(
  target: Set<string>,
  entryRunId: unknown,
  result?: ExecutionTestResult,
): void {
  [
    entryRunId,
    result?.run_id,
    ...((result?.variants || []) as ExecutionTestResult[]).map(variant => variant.run_id),
  ].forEach(candidate => {
    const text = String(candidate || '').trim();
    if (text) target.add(text);
  });
}

/* ─────────────────────────────────────────────
   Helpers
───────────────────────────────────────────── */
function shortHistoryTime(value?: string): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function historyLabel(kind: 'result' | 'actions', createdAt?: string, fallback?: string): string {
  const time = shortHistoryTime(createdAt);
  const base = kind === 'result' ? 'Previous result before rerun' : 'Previous script before edit';
  return time ? `${base} · ${time}` : (fallback || base);
}

function mergeActionSteps(
  parent?: Record<string, PwActionStep> | null,
  scoped?: Record<string, PwActionStep> | null,
): Record<string, PwActionStep> | null {
  if (!parent && !scoped) return null;
  return {
    ...(parent || {}),
    ...(scoped || {}),
  };
}

function resolvePass(test: ExecutionTestResult): boolean | null {
  if (test.pass !== undefined) return test.pass;
  const s = test.status?.toLowerCase();
  if (s === 'passed' || s === 'pass') return true;
  if (s === 'failed' || s === 'fail') return false;
  return null;
}

function statusPillClass(test: ExecutionTestResult): string {
  const variants = Array.isArray(test.variants) ? test.variants : [];
  const hasPassedVariant = variants.some(variant => resolvePass(variant) === true);
  const hasFailedVariant = variants.some(variant => resolvePass(variant) === false);
  if (hasPassedVariant && hasFailedVariant) return 'rrw-status-pill-partially_passed';
  const p = resolvePass(test);
  if (p === true) return 'rrw-status-pill-passed';
  if (p === false) return 'rrw-status-pill-failed';
  return 'rrw-status-pill-never_run';
}

function statusLabel(test: ExecutionTestResult): string {
  const variants = Array.isArray(test.variants) ? test.variants : [];
  const hasPassedVariant = variants.some(variant => resolvePass(variant) === true);
  const hasFailedVariant = variants.some(variant => resolvePass(variant) === false);
  if (hasPassedVariant && hasFailedVariant) return 'Partially Passed';
  const p = resolvePass(test);
  if (p === true) return 'Passed';
  if (p === false) return 'Failed';
  return 'Unknown';
}

/**
 * Stable 1-based ordinal for a data variant.
 *
 * The label must come from the variant's own identity (`…-STC-2`), not from its
 * position in the rendered list. A version that only contains the re-run variant
 * holds it at position 0, which would otherwise label variant 2 as "Variant 1"
 * and make every previous-version selection collapse onto the same entry.
 * Falls back to the position when no identity is available.
 */
export function variantOrdinal(variant?: ExecutionTestResult, fallbackIndex = 0): number {
  const source = String(variant?.variant_id || '').trim()
    || String(variant?.execution_key || '').split('::')[1]
    || '';
  const match = source.match(/(\d+)\s*$/);
  if (match) {
    const parsed = parseInt(match[1], 10);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return fallbackIndex + 1;
}

function formatVariantOption(variant: ExecutionTestResult, index: number): string {
  return `Variant ${variantOrdinal(variant, index)} · ${statusLabel(variant)}`;
}

function selectedVariantId(testKey?: string, variant?: ExecutionTestResult): string | null {
  if (!testKey || !variant) return null;
  const explicit = String(variant.variant_id || '').trim();
  if (explicit) return explicit;
  const candidates = [
    variant.execution_key,
    variant.evidence_key,
    variant.execution_output_key,
  ];
  for (const candidate of candidates) {
    const text = String(candidate || '').trim();
    if (text.startsWith(`${testKey}::`)) {
      const suffix = text.slice(testKey.length + 2).trim();
      if (suffix) return suffix;
    }
  }
  return null;
}

function variantExecutionKey(testKey?: string, variant?: ExecutionTestResult): string | null {
  if (!testKey || !variant) return null;
  const explicitExecutionKey = String(variant.execution_key || '').trim();
  if (explicitExecutionKey) return explicitExecutionKey;
  const variantId = selectedVariantId(testKey, variant);
  if (variantId) return `${testKey}::${variantId}`;
  return variant.version_number != null ? `${testKey}::v${variant.version_number}` : null;
}

function formatDuration(ms?: number | null): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function formatDatetime(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function resolveEvidenceUrl(url?: string | null): string | undefined {
  if (!url) return undefined;
  if (/^https?:\/\//i.test(url)) return url;
  return url.startsWith('/') ? url : `/${url}`;
}

function historyTime(entry: Pick<HistoryEntry, 'created_at'>): number {
  if (!entry.created_at) return 0;
  const time = new Date(entry.created_at).getTime();
  return Number.isNaN(time) ? 0 : time;
}

function actionsForResultVersion(
  resultEntry: HistoryEntry,
  actionEntries: HistoryEntry[],
  actionSnapshotsByRunId: Record<string, Record<string, Record<string, PwActionStep>>>,
  testKey?: string,
  activeActionCacheKey?: string,
  activeVariantExecutionKey?: string | null,
): Record<string, PwActionStep> | undefined {
  if (resultEntry.actions) return resultEntry.actions;
  const versionVariant = activeVariantExecutionKey
    ? resultEntry.result?.variants?.find(variant => (
      variant.execution_key === activeVariantExecutionKey
      || (variant.variant_id && `${testKey}::${variant.variant_id}` === activeVariantExecutionKey)
    ))
    : undefined;
  const snapshotRunId = versionVariant?.run_id || resultEntry.result?.run_id || resultEntry.run_id;
  const snapshotActions = snapshotRunId ? actionSnapshotsByRunId[snapshotRunId] : undefined;
  if (snapshotActions && testKey) {
    const parentActions = snapshotActions[testKey] || null;
    const scopedActions = activeActionCacheKey ? snapshotActions[activeActionCacheKey] || null : null;
    const fromSnapshot = activeVariantExecutionKey
      ? mergeActionSteps(parentActions, scopedActions)
      : (parentActions || scopedActions);
    if (fromSnapshot) return fromSnapshot;
  }
  const resultTime = historyTime(resultEntry);
  const rawResultSteps = resultEntry.result?.steps;
  const resultSteps = Array.isArray(rawResultSteps)
    ? (rawResultSteps as Array<StepResult | string>).filter((step): step is StepResult => (
      typeof step === 'object' && step !== null && !step.is_assertion
    ))
    : [];
  const candidates = actionEntries
    .filter(entry => entry.actions)
    .filter(entry => {
      const actionTime = historyTime(entry);
      return !resultTime || !actionTime || actionTime <= resultTime;
    })
    .sort((a, b) => historyTime(b) - historyTime(a));
  const candidate = candidates[0]?.actions;
  if (candidate && resultSteps.length > 0) {
    const mismatch = resultSteps.some((step, index) => {
      const stepKey = `step_${step.step_index || step.step_id || index + 1}`;
      const action = candidate[stepKey];
      const cachedText = String(action?.raw_action || action?.description || action?.url || '').trim();
      const resultText = String(step.action || '').trim();
      return cachedText && resultText && cachedText !== resultText;
    });
    if (!mismatch) return candidate;
  } else if (candidate) {
    return candidate;
  }
  if (!resultSteps.length) return candidate;
  return resultSteps.reduce<Record<string, PwActionStep>>((acc, step, index) => {
    const stepNum = Number(step.step_index || step.step_id || index + 1) || index + 1;
    acc[`step_${stepNum}`] = {
      type: 'HistoricalStep',
      raw_action: step.action || '',
      raw_expected: step.expected || '',
    };
    return acc;
  }, {});
}

function actionsForRunSnapshot(
  runId: string | null | undefined,
  actionSnapshotsByRunId: Record<string, Record<string, Record<string, PwActionStep>>>,
  testKey?: string,
  activeActionCacheKey?: string,
  activeVariantExecutionKey?: string | null,
): Record<string, PwActionStep> | undefined {
  if (!runId || !testKey) return undefined;
  const snapshotActions = actionSnapshotsByRunId[runId];
  if (!snapshotActions) return undefined;
  const parentActions = snapshotActions[testKey] || null;
  const scopedActions = activeActionCacheKey ? snapshotActions[activeActionCacheKey] || null : null;
  if (activeVariantExecutionKey) {
    return mergeActionSteps(parentActions, scopedActions) || undefined;
  }
  return parentActions || scopedActions || undefined;
}

function actionMapToTestCaseSteps(actionMap?: Record<string, PwActionStep>): Record<string, PwActionStep> | null {
  if (!actionMap || Object.keys(actionMap).length === 0) return null;
  return Object.entries(actionMap).reduce<Record<string, PwActionStep>>((acc, [stepKey, step]) => {
    acc[stepKey] = {
      raw_action: String(step.raw_action || step.description || step.action || step.url || ''),
      raw_expected: String(step.raw_expected || step.expected_result || step.expected || ''),
    };
    return acc;
  }, {});
}

/**
 * Keep resolved Playwright details (type, locator, values) while making the
 * current view's Verify text agree with the latest authored test-case step.
 * Run snapshots intentionally remain untouched for execution history.
 */
function withLatestExpectedResults(
  actionMap: Record<string, PwActionStep> | null | undefined,
  authoredSteps: Record<string, PwActionStep> | null | undefined,
): Record<string, PwActionStep> | null | undefined {
  if (!actionMap || !authoredSteps) return actionMap;
  return Object.entries(actionMap).reduce<Record<string, PwActionStep>>((merged, [stepKey, action]) => {
    const authored = authoredSteps[stepKey];
    merged[stepKey] = authored && Object.prototype.hasOwnProperty.call(authored, 'raw_expected')
      ? { ...action, raw_expected: authored.raw_expected || '' }
      : action;
    return merged;
  }, {});
}

function hasMatchingCurrentAssertion(
  runActions: Record<string, PwActionStep> | undefined,
  authoredSteps: Record<string, PwActionStep> | null,
  stepKey: string,
): boolean {
  if (!runActions || !authoredSteps) return true;
  const authored = authoredSteps[stepKey];
  if (!authored || !Object.prototype.hasOwnProperty.call(authored, 'raw_expected')) return true;
  return String(runActions[stepKey]?.raw_expected || '') === String(authored.raw_expected || '');
}

function resultToTestCaseSteps(result?: ExecutionTestResult | null): Record<string, PwActionStep> | null {
  const rawSteps = result?.steps;
  if (!Array.isArray(rawSteps) || rawSteps.length === 0) return null;
  const steps = (rawSteps as Array<StepResult | string>).filter((step): step is StepResult | string => (
    typeof step === 'string' || (typeof step === 'object' && step !== null && !(step as StepResult).is_assertion)
  ));
  if (steps.length === 0) return null;
  return steps.reduce<Record<string, PwActionStep>>((acc, step, index) => {
    if (typeof step === 'string') {
      acc[`step_${index + 1}`] = { raw_action: step, raw_expected: '' };
      return acc;
    }
    const stepNumber = Number(step.step_index || step.step_id || index + 1) || index + 1;
    acc[`step_${stepNumber}`] = {
      raw_action: step.action || '',
      raw_expected: step.expected || '',
    };
    return acc;
  }, {});
}

function formatActionUpdateSummaryLabel(summaries: ActionUpdateSummary[]): string | null {
  if (!summaries.length) return null;
  const sorted = [...summaries].sort((a, b) => a.step_number - b.step_number);
  const version = sorted[0]?.version_number;
  if (sorted.length === 1) {
    return sorted[0].label || `Step ${sorted[0].step_number} action updated in v${version}`;
  }
  const steps = sorted.map(item => item.step_number).join(', ');
  return `Steps ${steps} actions updated in v${version}`;
}

function resultIdentityKey(result: ExecutionTestResult, index: number): string {
  return String(
    result.evidence_key
    || result.execution_key
    || `${result.test_case_key || 'test'}::${result.variant_id || result.run_id || index}`
  ).trim();
}

/* ─────────────────────────────────────────────
   Component
───────────────────────────────────────────── */
export default function ExecutionTestPopup({ test, runMeta, onClose, onRunStarted, onTestRerun, onActionsUpdated }: Props): React.ReactElement {
  const {
    flags: featureFlags,
    loading: featureFlagsLoading,
    refresh: refreshFeatureFlags,
  } = useActiveFeatureFlags();
  const testExecutionActionDisabled = featureFlagsLoading || !featureFlags.test_execution;
  const [activeTab, setActiveTab] = useState<PopupTab>('summary');
  const variants = React.useMemo(() => (
    Array.isArray(test.variants) ? test.variants : []
  ), [test.variants]);
  const [selectedVariantIdx, setSelectedVariantIdx] = useState(0);
  const latestParentHistoryEntry = React.useMemo(() => (
    (test.result_history || [])
      .slice()
      .sort((a, b) => historyTime(b) - historyTime(a))[0]
  ), [test.result_history]);
  const currentVersionVariants = React.useMemo(() => {
    if (variants.length === 0) return [];
    const previousVariantsRaw = latestParentHistoryEntry?.result?.variants;
    const previousVariants = Array.isArray(previousVariantsRaw) && previousVariantsRaw.length > 0
      ? previousVariantsRaw
      : latestParentHistoryEntry?.result
        ? [latestParentHistoryEntry.result]
        : [];
    const previousIdentityKeys = new Set(previousVariants.map((variant, index) => resultIdentityKey(variant, index)));
    const previousRunIds = new Set(
      previousVariants
        .map(variant => String(variant.run_id || latestParentHistoryEntry?.run_id || '').trim())
        .filter(Boolean)
    );

    const changedVariants = variants.filter((variant, index) => {
      const identityKey = resultIdentityKey(variant, index);
      if (!previousIdentityKeys.has(identityKey)) return true;
      const runId = String(variant.run_id || '').trim();
      return !!runId && !previousRunIds.has(runId);
    });
    if (changedVariants.length > 0) return changedVariants;

    const fallbackRunId = runMeta.run_id || variants.find(variant => variant.run_id)?.run_id || test.run_id || null;
    if (!fallbackRunId) return variants;
    const scoped = variants.filter(variant => (variant.run_id || null) === fallbackRunId);
    return scoped.length > 0 ? scoped : variants;
  }, [latestParentHistoryEntry, runMeta.run_id, test.run_id, variants]);
  const activeVariantIdx = currentVersionVariants.length > 0 ? Math.min(selectedVariantIdx, currentVersionVariants.length - 1) : 0;
  const activeResult = currentVersionVariants.length > 0 ? currentVersionVariants[activeVariantIdx] : test;
  // A parent snapshot is the only history entry that preserves the outcome of
  // every variant together (for example, a partially-passed Version 1).
  const historyResult = variants.length > 0 && (test.result_history?.length ?? 0) > 0
    ? test
    : activeResult;
  const testKey = test.test_case_key;
  const activeVariantExecutionKey = variantExecutionKey(testKey, variants.length > 0 ? activeResult : undefined);
  const activeActionCacheKey = activeVariantExecutionKey || testKey;
  const [actionHistory, setActionHistory] = useState<Array<HistoryEntry>>([]);
  const [actionUpdateSummaries, setActionUpdateSummaries] = useState<Record<string, ActionUpdateSummary[]>>({});
  const [actionSnapshotsByRunId, setActionSnapshotsByRunId] = useState<Record<string, Record<string, Record<string, PwActionStep>>>>({});
  const [testCaseSteps, setTestCaseSteps] = useState<Record<string, PwActionStep> | null>(null);
  const [selectedHistoryId, setSelectedHistoryId] = useState('current');

  const resultHistoryEntries = useMemo<HistoryEntry[]>(() => (
    (historyResult.result_history || [])
      .map((entry, index) => ({
        id: `result:${entry.id || index}`,
        label: historyLabel('result', entry.created_at, entry.label),
        kind: 'result' as const,
        created_at: entry.created_at,
        run_id: entry.run_id || entry.result?.run_id || null,
        evidence_key: entry.evidence_key || entry.result?.evidence_key || null,
        execution_key: entry.execution_key || entry.result?.execution_key || null,
        variant_id: entry.variant_id || entry.result?.variant_id || null,
        result: entry.result ? {
          ...entry.result,
          run_id: entry.run_id || entry.result.run_id || undefined,
          runner_blob_prefix: entry.runner_blob_prefix || entry.result.runner_blob_prefix || undefined,
          evidence_key: entry.evidence_key || entry.result.evidence_key,
          execution_key: entry.execution_key || entry.result.execution_key,
          variant_id: entry.variant_id || entry.result.variant_id,
        } : undefined,
      }))
      .filter(entry => !!entry.result)
  ), [historyResult.result_history]);

  const historyEntries = useMemo<HistoryEntry[]>(() => {
    // Walking oldest → newest lets each version see only the runs that came
    // before it, which is what identifies the variants it actually executed.
    const runIdsSoFar = new Set<string>();
    const previousResultVersions = resultHistoryEntries
      .slice()
      .sort((a, b) => historyTime(a) - historyTime(b))
      .map((entry, index) => {
        const allVariants = (entry.result?.variants || []) as ExecutionTestResult[];
        const scoped = scopeVariantsToVersion(allVariants, runIdsSoFar);
        collectVersionRunIds(runIdsSoFar, entry.run_id, entry.result);
        return {
          ...entry,
          label: `Version ${index + 1}`,
          version_number: index + 1,
          variants: scoped,
          actions: actionsForResultVersion(
            entry,
            actionHistory,
            actionSnapshotsByRunId,
            testKey,
            activeActionCacheKey,
            activeVariantExecutionKey,
          ),
        };
      });
    const currentVersion = previousResultVersions.length + 1;
    const currentEntry: HistoryEntry = {
      id: 'current',
      label: `Version ${currentVersion}`,
      kind: 'current' as const,
      version_number: currentVersion,
      run_id: activeResult.run_id || runMeta.run_id || null,
      evidence_key: activeResult.evidence_key || null,
      execution_key: activeResult.execution_key || null,
      variant_id: activeResult.variant_id || null,
    };
    return [currentEntry, ...previousResultVersions.slice().reverse()];
  }, [actionHistory, actionSnapshotsByRunId, activeActionCacheKey, activeResult.evidence_key, activeResult.execution_key, activeResult.run_id, activeResult.variant_id, activeVariantExecutionKey, resultHistoryEntries, runMeta.run_id, testKey]);

  const selectedHistory = historyEntries.find(entry => entry.id === selectedHistoryId) || historyEntries[0];
  const isHistorySelected = selectedHistory.id !== 'current';
  const historicalResult = selectedHistory.result;
  const versionVariants = React.useMemo(() => {
    // `selectedHistory.variants` is already scoped to the variants that ran in
    // that version; the raw snapshot always carries the full list.
    if (selectedHistory.variants?.length) return selectedHistory.variants;
    if (historicalResult?.variants?.length) return historicalResult.variants;
    if (historicalResult && variants.length > 0) return [historicalResult];
    return currentVersionVariants;
  }, [currentVersionVariants, historicalResult, selectedHistory.variants, variants.length]);
  const selectedVersionVariantIdx = versionVariants.length > 0 ? Math.min(selectedVariantIdx, versionVariants.length - 1) : 0;
  const selectedVersionVariant = versionVariants.length > 0 ? versionVariants[selectedVersionVariantIdx] : null;
  // Label by identity, following whichever variant is actually on screen, so the
  // header and the "Re-run Variant N" button cannot disagree with the dropdown.
  const activeVariantOrdinal = variantOrdinal(
    selectedVersionVariant || activeResult,
    selectedVersionVariantIdx,
  );
  const displayedResult = historicalResult && variants.length > 0
    ? selectedVersionVariant
      || historicalResult.variants?.find(variant => (
        variant.execution_key === activeResult.execution_key
        || variant.variant_id === activeResult.variant_id
      ))
      || historicalResult
    : selectedVersionVariant || historicalResult || activeResult;
  const versionResult = historicalResult || test;
  const evidenceRequestKey = useMemo(() => ([
    runMeta.project_id || '',
    runMeta.job_id || '',
    displayedResult.test_case_key || activeResult.test_case_key || test.test_case_key || '',
    displayedResult.evidence_key || '',
    displayedResult.run_id || selectedHistory.run_id || '',
    displayedResult.variant_id || activeResult.variant_id || '',
    selectedHistory.id,
  ].join('|')), [
    activeResult.test_case_key,
    activeResult.variant_id,
    displayedResult.evidence_key,
    displayedResult.run_id,
    displayedResult.test_case_key,
    displayedResult.variant_id,
    runMeta.job_id,
    runMeta.project_id,
    selectedHistory.id,
    selectedHistory.run_id,
    test.test_case_key,
  ]);

  // Evidence fetched lazily when the Evidence tab is first opened
  const [fetchedEvidence, setFetchedEvidence] = useState<{
    primary_screenshot_url?: string | null;
    files?: Array<{ name: string; kind: string; relative_path: string; download_url?: string }>;
  } | null>(null);
  const [evidenceLoading, setEvidenceLoading] = useState(false);
  const [evidenceFetched, setEvidenceFetched] = useState(false);

  useEffect(() => {
    setSelectedVariantIdx(0);
  }, [selectedHistoryId]);

  useEffect(() => {
    if (selectedVariantIdx >= versionVariants.length && versionVariants.length > 0) {
      setSelectedVariantIdx(Math.max(0, versionVariants.length - 1));
    }
  }, [selectedVariantIdx, versionVariants.length]);
  const [selectedShotIdx, setSelectedShotIdx] = useState<number>(0);

  useEffect(() => {
    setFetchedEvidence(null);
    setEvidenceFetched(false);
    setSelectedShotIdx(0);
  }, [evidenceRequestKey]);

  // Reset selected screenshot index each time evidence tab is opened fresh
  useEffect(() => {
    if (activeTab === 'evidence' && !evidenceFetched) setSelectedShotIdx(0);
  }, [activeTab, evidenceFetched]);

  useEffect(() => {
    if (activeTab !== 'evidence' || evidenceFetched) return;
    const { project_id, job_id } = runMeta;
    const evidenceTestKey = displayedResult.test_case_key || activeResult.test_case_key || test.test_case_key;
    if (!project_id || !job_id || !evidenceTestKey) {
      setEvidenceFetched(true);
      return;
    }

    let cancelled = false;
    setEvidenceLoading(true);

    const loadEvidence = async () => {
      const evidenceUrl = API_ENDPOINTS.PLAYWRIGHT_TEST_EVIDENCE(project_id, job_id, evidenceTestKey);
      const params = new URLSearchParams();
      if (displayedResult.evidence_key) params.set('evidence_key', displayedResult.evidence_key);
      const evidenceRunId = displayedResult.run_id || selectedHistory.run_id;
      if (evidenceRunId) params.set('evidence_run_id', evidenceRunId);
      const variantId = displayedResult.variant_id || activeResult.variant_id;
      if (variantId) params.set('variant_id', variantId);
      const query = params.toString();
      try {
        const res = await apiClient.get(query ? `${evidenceUrl}?${query}` : evidenceUrl);
        if (!cancelled) setFetchedEvidence(res.data);
      } catch {
        if (!cancelled) setFetchedEvidence(null);
      } finally {
        if (!cancelled) {
          setEvidenceLoading(false);
          setEvidenceFetched(true);
        }
      }
    };

    void loadEvidence();
    return () => { cancelled = true; };
  }, [activeTab, evidenceFetched, evidenceRequestKey, runMeta, test.test_case_key, activeResult.test_case_key, activeResult.variant_id, displayedResult.test_case_key, displayedResult.evidence_key, displayedResult.run_id, displayedResult.variant_id, selectedHistory.run_id]);

  const title = test.title || test.name || test.test_case_key || 'Test Result';
  const failureChatContext = useMemo(() => {
    if (!runMeta.project_id) {
      return null;
    }

    return {
      context_mode: 'run',
      artifact_id: runMeta.artifact_id ?? null,
      job_id: runMeta.job_id ?? null,
      run_id: runMeta.run_id ?? null,
      test_key: displayedResult.test_case_key || activeResult.test_case_key || test.test_case_key || null,
      execution_key: displayedResult.execution_key || activeResult.execution_key || null,
      variant_id: displayedResult.variant_id || activeResult.variant_id || null,
      dataset_id: displayedResult.dataset_id || activeResult.dataset_id || null,
    } as const;
  }, [
    activeResult.dataset_id,
    activeResult.execution_key,
    activeResult.test_case_key,
    activeResult.variant_id,
    displayedResult.dataset_id,
    displayedResult.execution_key,
    displayedResult.test_case_key,
    displayedResult.variant_id,
    runMeta.artifact_id,
    runMeta.job_id,
    runMeta.project_id,
    runMeta.run_id,
    test.test_case_key,
  ]);

  // primaryScreenshot is used only in Summary tab's "Final Screenshot" block
  const primaryScreenshot = resolveEvidenceUrl(displayedResult.evidence?.primary_screenshot_url);

  const TABS: Array<{ id: PopupTab; label: string }> = [
    { id: 'summary',   label: 'Summary' },
    ...(variants.length > 0 ? [{ id: 'data' as PopupTab, label: 'Data Used' }] : []),
    { id: 'tc_steps',  label: 'Test Case Steps' },
    { id: 'steps',     label: 'Execution Steps' },
    { id: 'actions',   label: 'Automated Scripts' },
    { id: 'evidence',  label: 'Evidence' },
    { id: 'execution', label: 'Execution Log' },
    { id: 'chat',      label: 'Failure Analysis Chat' },
    { id: 'timing',    label: 'Timing' },
  ];

  /* ───────── Fuzzy combobox state for action type per step ───────── */
  const [fuzzyQuery, setFuzzyQuery] = React.useState<Record<string, string>>({});
  const [fuzzyOpen, setFuzzyOpen] = React.useState<Record<string, boolean>>({});
  const [actionsLoading, setActionsLoading] = useState(false);
  const [actionsError, setActionsError] = useState<string | null>(null);
  const [actions, setActions] = useState<Record<string, PwActionStep> | null>(null);
  const [draft, setDraft] = useState<Record<string, PwActionStep>>({});
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [actionsFetched, setActionsFetched] = useState(false);
  const actionsRequestRef = useRef(0);
  const [rerunning, setRerunning] = useState(false);
  const [rerunError, setRerunError] = useState<string | null>(null);
  const [authMode, setAuthMode] = useState('');
  const [savedAuthMode, setSavedAuthMode] = useState('');
  const [authLoading, setAuthLoading] = useState(true);
  const [authSaving, setAuthSaving] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const authModeUrl = runMeta.project_id && runMeta.artifact_id && testKey
    ? API_ENDPOINTS.PLAYWRIGHT_TEST_AUTH_MODE(runMeta.project_id, runMeta.artifact_id, testKey)
    : null;

  useEffect(() => {
    let active = true;
    setAuthLoading(true);
    setAuthError(null);
    if (!authModeUrl) { setAuthLoading(false); return; }
    apiClient.get(authModeUrl).then(res => {
      if (!active) return;
      setAuthMode(res.data.auth_mode || '');
      setSavedAuthMode(res.data.auth_mode || '');
    }).catch(() => { if (active) setAuthError('Could not load the starting session setting.'); })
      .finally(() => { if (active) setAuthLoading(false); });
    return () => { active = false; };
  }, [authModeUrl]);

  const saveAuthMode = async (): Promise<boolean> => {
    if (!authModeUrl || authLoading) return false;
    setAuthSaving(true);
    setAuthError(null);
    try {
      await apiClient.patch(authModeUrl, { auth_mode: authMode || null });
      setSavedAuthMode(authMode);
      return true;
    } catch {
      setAuthError('Could not save the starting session setting.');
      return false;
    } finally { setAuthSaving(false); }
  };
  const [expandedErrors, setExpandedErrors] = useState<Record<string, boolean>>({});

  const stepsArray = React.useMemo(() => {
    const recorded = (displayedResult.steps || []) as StepResult[];
    if (!actions || Object.keys(actions).length === 0) return recorded;

    // A post-action assertion result is emitted with the SAME step_index as the
    // action it validates. Keying both into one map slot silently destroys the
    // action row, which makes its verify block attach to the previous step and
    // shifts every later step up by one. Only real action steps define which
    // step numbers were executed.
    const executedStepNumbers = new Set<number>();
    recorded.forEach((step, index) => {
      if (step && step.is_assertion) return;
      const number = Number(step.step_index ?? step.step_id);
      executedStepNumbers.add(Number.isFinite(number) && number > 0 ? number : index + 1);
    });

    const notExecuted: StepResult[] = [];
    Object.entries(actions)
      .sort(([a], [b]) => (parseInt(a.replace(/\D/g, ''), 10) || 0) - (parseInt(b.replace(/\D/g, ''), 10) || 0))
      .forEach(([stepKey, action], index) => {
        const stepNumber = parseInt(stepKey.replace(/\D/g, ''), 10) || index + 1;
        if (!executedStepNumbers.has(stepNumber)) {
          notExecuted.push({
            step_id: stepNumber,
            step_index: stepNumber,
            action: action.raw_action || '',
            expected: action.raw_expected || '',
            success: false,
            not_executed: true,
            message: 'Not executed because an earlier step failed or was deferred.',
          });
        }
      });
    if (notExecuted.length === 0) return recorded;

    // Stable sort by step number so each assertion stays directly behind the
    // action it belongs to, which is what the render grouping relies on.
    return [...recorded, ...notExecuted]
      .map((step, order) => ({ step, order }))
      .sort((a, b) => {
        const na = Number(a.step.step_index ?? a.step.step_id) || 0;
        const nb = Number(b.step.step_index ?? b.step.step_id) || 0;
        return na === nb ? a.order - b.order : na - nb;
      })
      .map(({ step }) => step);
  }, [actions, displayedResult.steps]);

  /** Map from cache step key to its assertion result. */
  const assertionByStepKey = React.useMemo(() => {
    const map: Record<string, StepResult> = {};
    stepsArray.forEach(s => {
      if (typeof s === 'object' && s.is_assertion && s.step_id) {
        map[String(s.step_id).replace(/_assert$/, '')] = s;
      }
    });
    return map;
  }, [stepsArray]);
  const passedSteps = stepsArray.filter(s => typeof s === 'object' && !(s as StepResult).not_executed && (s as StepResult).success === true).length;
  const failedSteps = stepsArray.filter(s => typeof s === 'object' && !(s as StepResult).not_executed && (s as StepResult).success === false).length;

  useEffect(() => {
    setActions(null);
    setDraft({});
    setEditing(false);
    setSaveError(null);
    setRerunError(null);
    setActionsFetched(false);
    setActionHistory([]);
    setActionUpdateSummaries({});
    setActionSnapshotsByRunId({});
    setTestCaseSteps(null);
  }, [activeActionCacheKey, activeResult.run_id]);

  useEffect(() => {
    // Fetch actions once when the popup opens so the History dropdown can include edit snapshots.
    if (actionsFetched) return;
    const projectId = runMeta.project_id;
    const artifactId = runMeta.artifact_id;
    if (!projectId || !artifactId || !testKey || !activeActionCacheKey) {
      setActionsFetched(true);
      return;
    }

    const requestId = ++actionsRequestRef.current;
    setActionsLoading(true);
    setActionsError(null);

    const loadActions = async () => {
      try {
        const res = await apiClient.get(
          API_ENDPOINTS.PLAYWRIGHT_RESOLVED_ACTIONS(projectId, artifactId, runMeta.job_id || undefined)
        );
        if (requestId !== actionsRequestRef.current) return;
        const data = (res.data ?? {}) as ResolvedActionsResponse;
        const all = (data.actions ?? {}) as Record<string, Record<string, PwActionStep>>;
        const parentActions = all[testKey] || null;
        const scopedActions = all[activeActionCacheKey] || null;
        const forKey = activeVariantExecutionKey
          ? mergeActionSteps(parentActions, scopedActions)
          : (parentActions || scopedActions);
        const snapshotsByRunId = data.action_snapshots_by_run_id ?? {};
        const currentRunActions = actionsForRunSnapshot(
          activeResult.run_id || runMeta.run_id || null,
          snapshotsByRunId,
          testKey,
          activeActionCacheKey,
          activeVariantExecutionKey,
        );
        const currentAuthoredSteps = data.test_case_steps?.[testKey] || null;
        const initialActions = withLatestExpectedResults(currentRunActions || forKey, currentAuthoredSteps);
        // A parent snapshot preserves the complete prior version; a
        // variant-specific key may only contain its override actions.
        const histories = data.action_history?.[activeActionCacheKey]
          || data.action_history?.[testKey]
          || [];
        const mappedHistory = histories
          .map((entry, index) => ({
            id: `actions:${entry.id || index}`,
            label: historyLabel('actions', entry.created_at, entry.label),
            kind: 'actions' as const,
            created_at: entry.created_at,
            actions: activeVariantExecutionKey
              ? (mergeActionSteps(parentActions, entry.actions || null) || undefined)
              : entry.actions,
          }))
          .filter(entry => !!entry.actions);
        setActions(initialActions);
        setTestCaseSteps(currentAuthoredSteps);
        setActionHistory(mappedHistory);
        setActionUpdateSummaries(data.action_update_summaries ?? {});
        setActionSnapshotsByRunId(snapshotsByRunId);
        setDraft(initialActions ? JSON.parse(JSON.stringify(initialActions)) : {});
      } catch (err: any) {
        if (requestId !== actionsRequestRef.current) return;
        setActionsError(err?.response?.data?.detail || err?.message || 'Failed to load actions.');
      } finally {
        if (requestId === actionsRequestRef.current) {
          setActionsLoading(false);
          setActionsFetched(true);
        }
      }
    };

    void loadActions();
  }, [activeActionCacheKey, activeResult.run_id, activeVariantExecutionKey, actionsFetched, isHistorySelected, runMeta.artifact_id, runMeta.job_id, runMeta.project_id, runMeta.run_id, testKey]);

  const updateDraftField = (stepKey: string, field: string, value: string) => {
    setDraft(prev => ({
      ...prev,
      [stepKey]: { ...(prev[stepKey] || {}), [field]: value },
    }));
  };

  const cancelEdit = () => {
    setDraft(actions ? JSON.parse(JSON.stringify(actions)) : {});
    setEditing(false);
    setSaveError(null);
  };

  /** Insert a blank NavigateAction step immediately after `afterKey`, renumbering all subsequent steps. */
  const insertStepAfter = (afterKey: string) => {
    setDraft(prev => {
      const sorted = Object.entries(prev).sort(([a], [b]) =>
        (parseInt(a.replace(/\D/g, ''), 10) || 0) - (parseInt(b.replace(/\D/g, ''), 10) || 0)
      );
      const insertIdx = sorted.findIndex(([k]) => k === afterKey);
      const blankStep = { type: 'NavigateAction', url: '', raw_action: '', raw_expected: '' } as PwActionStep;
      const updated: [string, PwActionStep][] = [
        ...sorted.slice(0, insertIdx + 1),
        ['__new__', blankStep],
        ...sorted.slice(insertIdx + 1),
      ];
      const result: Record<string, PwActionStep> = {};
      updated.forEach(([, step], i) => { result[`step_${i + 1}`] = step; });
      return result;
    });
  };

  /** Remove `deleteKey` and renumber remaining steps sequentially from step_1. */
  const deleteStep = (deleteKey: string) => {
    setDraft(prev => {
      if (Object.keys(prev).length <= 1) return prev; // keep at least one step
      const sorted = Object.entries(prev)
        .sort(([a], [b]) => (parseInt(a.replace(/\D/g, ''), 10) || 0) - (parseInt(b.replace(/\D/g, ''), 10) || 0))
        .filter(([k]) => k !== deleteKey);
      const result: Record<string, PwActionStep> = {};
      sorted.forEach(([, step], i) => { result[`step_${i + 1}`] = step; });
      return result;
    });
  };

  const saveActions = async (): Promise<boolean> => {
    const { project_id, artifact_id } = runMeta;
    if (!project_id || !artifact_id || !testKey || !activeActionCacheKey) {
      setSaveError('Missing project, artifact, or test key.');
      return false;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const res = await apiClient.patch(
        API_ENDPOINTS.PLAYWRIGHT_PATCH_TEST_ACTIONS(project_id, artifact_id, activeActionCacheKey),
        { steps: draft },
      );
      const updatedSummaries = (res.data?.action_update_summaries ?? {}) as Record<string, ActionUpdateSummary[]>;
      const summariesForKey = updatedSummaries[activeActionCacheKey] || updatedSummaries[testKey] || [];
      setActions(JSON.parse(JSON.stringify(draft)));
      setActionUpdateSummaries(updatedSummaries);
      onActionsUpdated?.(activeActionCacheKey, summariesForKey, updatedSummaries);
      setEditing(false);
      return true;
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Failed to save actions.');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const rerunSingleTest = async () => {
    const { project_id, job_id, artifact_id, base_url } = runMeta;
    if (!project_id || !job_id || !artifact_id || !testKey) {
      setRerunError('Missing project, job, artifact, or test key.');
      return;
    }
    if (featureFlagsLoading) {
      void refreshFeatureFlags();
      setRerunError('Checking test execution access. Please try again in a moment.');
      return;
    }
    if (!featureFlags.test_execution) {
      setRerunError(TEST_EXECUTION_DISABLED_MESSAGE);
      return;
    }
    if (isHistorySelected) {
      setRerunError('Switch to Current before re-running this variant.');
      return;
    }
    setRerunning(true);
    setRerunError(null);
    try {
      // If there are unsaved edits, save them first.
      if (authLoading || authError) {
        setRerunError('Load or save the starting session setting before rerunning.');
        return;
      }
      if (authMode !== savedAuthMode && !(await saveAuthMode())) return;
      if (editing) {
        const ok = await saveActions();
        if (!ok) return;
      }
      const rerunRes = await apiClient.post<{ rerun_job_id?: number }>(API_ENDPOINTS.PLAYWRIGHT_RERUN_TEST(project_id, job_id), {
        test_key: testKey,
        artifact_id,
        base_url: base_url ?? undefined,
        variant_id: variants.length > 0 ? selectedVariantId(testKey, activeResult) || undefined : undefined,
        // The backend resolves this against the parent run's full variant list,
        // while `activeVariantIdx` indexes the filtered current-version list.
        // Send the position in the full list so the fallback path (used when
        // variant_id is absent) targets the same variant.
        variant_index: variants.length > 0
          ? Math.max(variants.indexOf(activeResult), 0)
          : undefined,
      });
      const rerunKey = activeActionCacheKey || testKey;
      onTestRerun?.(rerunKey);
      onRunStarted?.(rerunRes.data?.rerun_job_id, rerunKey);
      onClose();
    } catch (err: any) {
      setRerunError(err?.response?.data?.detail || err?.message || 'Failed to start rerun.');
    } finally {
      setRerunning(false);
    }
  };

  // Result-history entries do not always contain a separate action snapshot.
  // In that case use the artifact-wide historical cache loaded above; the
  // previous code rendered an empty object and showed "no scripts" until a
  // variant change happened to refresh the parent key.
  const currentRunActions = React.useMemo(() => (
    actionsForRunSnapshot(
      activeResult.run_id || runMeta.run_id || null,
      actionSnapshotsByRunId,
      testKey,
      activeActionCacheKey,
      activeVariantExecutionKey,
    )
  ), [actionSnapshotsByRunId, activeActionCacheKey, activeResult.run_id, activeVariantExecutionKey, runMeta.run_id, testKey]);
  const currentActionsWithLatestVerify = React.useMemo(() => (
    withLatestExpectedResults(currentRunActions || draft, testCaseSteps) || {}
  ), [currentRunActions, draft, testCaseSteps]);
  // A historical version must resolve its script from the variant that is
  // actually on screen, not from the current version's variant list.  The two
  // lists differ in length/order, so the shared index points at a different
  // variant and the run id collapses to the parent run — which is how an older
  // version started rendering a newer run's actions.
  const historyVariantExecutionKey = React.useMemo(() => (
    variantExecutionKey(testKey, selectedVersionVariant || undefined)
  ), [selectedVersionVariant, testKey]);
  const historyRunActions = React.useMemo(() => {
    if (!isHistorySelected) return undefined;
    const runId = selectedVersionVariant?.run_id
      || historicalResult?.run_id
      || selectedHistory.run_id
      || null;
    return actionsForRunSnapshot(
      runId,
      actionSnapshotsByRunId,
      testKey,
      historyVariantExecutionKey || testKey,
      historyVariantExecutionKey,
    );
  }, [
    actionSnapshotsByRunId,
    historicalResult?.run_id,
    historyVariantExecutionKey,
    isHistorySelected,
    selectedHistory.run_id,
    selectedVersionVariant,
    testKey,
  ]);
  const displayedActions = isHistorySelected
    ? (historyRunActions || selectedHistory.actions || {})
    : (editing ? draft : currentActionsWithLatestVerify);
  const historicalTestCaseSteps = React.useMemo(() => (
    resultToTestCaseSteps(displayedResult)
    || actionMapToTestCaseSteps(selectedHistory.actions)
    || null
  ), [displayedResult, selectedHistory.actions]);
  const displayedTestCaseSteps = isHistorySelected
    ? (historicalTestCaseSteps || {})
    : (testCaseSteps || actionMapToTestCaseSteps(actions || undefined) || {});
  const activeActionUpdateSummaries = React.useMemo(() => {
    if (isHistorySelected) return [];
    const variantKeys = currentVersionVariants
      .map(variant => variantExecutionKey(testKey, variant))
      .filter((key): key is string => !!key);
    const variantSummaries = variantKeys.flatMap(key => actionUpdateSummaries[key] || []);
    const relevantKeys = new Set<string>();
    if (variantSummaries.length > 0) {
      variantKeys.forEach(key => relevantKeys.add(key));
    } else {
      if (activeActionCacheKey) relevantKeys.add(activeActionCacheKey);
      if (testKey) relevantKeys.add(testKey);
    }

    const merged: ActionUpdateSummary[] = [];
    const seen = new Set<string>();
    relevantKeys.forEach(key => {
      (actionUpdateSummaries[key] || []).forEach(summary => {
        const dedupeKey = `${summary.step_key}:${summary.version_number}:${summary.current_type || ''}`;
        if (seen.has(dedupeKey)) return;
        seen.add(dedupeKey);
        merged.push(summary);
      });
    });
    return merged.sort((a, b) => a.step_number - b.step_number);
  }, [actionUpdateSummaries, activeActionCacheKey, currentVersionVariants, isHistorySelected, testKey]);
  const activeActionUpdateLabel = React.useMemo(
    () => formatActionUpdateSummaryLabel(activeActionUpdateSummaries),
    [activeActionUpdateSummaries],
  );
  const actionUpdateByStepKey = React.useMemo(() => {
    const map: Record<string, ActionUpdateSummary> = {};
    activeActionUpdateSummaries.forEach(summary => {
      if (summary.step_key) map[summary.step_key] = summary;
    });
    return map;
  }, [activeActionUpdateSummaries]);
  const canEditCurrent = !isHistorySelected;

  /** Standard action types shown in the dropdown. */
  const ACTION_TYPES_STANDARD = [
    'NavigateAction',
    'ClickAction',
    'FillAction',
    'SelectAction',
    'AssertVisibleAction',
    'AssertTextAction',
    'AssertPageContainsAction',
    'WaitAction',
    'NetworkAssertAction',
    'ScreenshotAction',
  ] as const;
  const ACTION_TYPES_ADVANCED = ['FillAllRequiredAction', 'CompoundAction'] as const;
  const ALL_ACTION_TYPES = [...ACTION_TYPES_STANDARD, ...ACTION_TYPES_ADVANCED];

  const LOCATOR_STRATEGIES = ['id', 'name', 'css', 'xpath', 'role', 'text', 'placeholder', 'data-testid'];

  /** Fields displayed/edited per Playwright action type. */
  const fieldsByType: Record<string, string[]> = {
    NavigateAction:           ['url', 'wait_until'],
    ClickAction:              ['locator_strategy', 'locator_value'],
    FillAction:               ['locator_strategy', 'locator_value', 'value'],
    SelectAction:             ['locator_strategy', 'locator_value', 'option_value'],
    AssertVisibleAction:      ['locator_strategy', 'locator_value'],
    AssertTextAction:         ['locator_strategy', 'locator_value', 'expected_text'],
    AssertPageContainsAction: ['text', 'should_exist'],
    WaitAction:               ['condition', 'locator_strategy', 'locator_value', 'timeout'],
    NetworkAssertAction:      ['assertion_type', 'endpoint_pattern', 'expected_status', 'method_filter'],
    ScreenshotAction:         ['label'],
    FillAllRequiredAction:    ['submit_after'],
    CompoundAction:           [],
  };

  /** When user picks a new action type, preserve overlapping fields and blank-init new ones. */
  const handleActionTypeChange = (stepKey: string, newType: string, step: PwActionStep) => {
    const newFields = fieldsByType[newType] || [];
    const next: Record<string, unknown> = { type: newType };
    if (step.raw_action)   next['raw_action']   = step.raw_action;
    if (step.raw_expected) next['raw_expected'] = step.raw_expected;
    if (newType === 'CompoundAction') {
      const existingSubActions = getCompoundSubActions(step);
      next['sub_actions'] = existingSubActions.length > 0 ? existingSubActions : [makeBlankSubAction()];
    }
    newFields.forEach(f => {
      next[f] = ((step as any)[f] !== undefined && (step as any)[f] !== '') ? (step as any)[f] : '';
    });
    setDraft(prev => ({ ...prev, [stepKey]: next as PwActionStep }));
  };
  const summarizeStep = (s: PwActionStep): string => {
    if (s.url) return s.url;
    if (s.locator_strategy && s.locator_value) {
      const base = `${s.locator_strategy}="${s.locator_value}"`;
      return s.value ? `${base} \u2192 "${s.value}"` : base;
    }
    return s.value || s.raw_action || '\u2014';
  };

  const getCompoundSubActions = (s: PwActionStep): PwActionStep[] => {
    const raw = Array.isArray((s as any).sub_actions) ? (s as any).sub_actions : [];
    return (raw as unknown[]).filter((item): item is PwActionStep => !!item && typeof item === 'object');
  };

  const makeBlankSubAction = (): PwActionStep => ({
    type: 'ClickAction',
    locator_strategy: 'css',
    locator_value: '',
    raw_action: '',
  });

  const setCompoundSubActions = (stepKey: string, subActions: PwActionStep[]) => {
    setDraft(prev => ({
      ...prev,
      [stepKey]: {
        ...(prev[stepKey] || {}),
        type: 'CompoundAction',
        sub_actions: subActions,
      },
    }));
  };

  const updateCompoundSubActionField = (stepKey: string, subIndex: number, field: string, value: string) => {
    const parent = draft[stepKey] || {};
    const subActions = getCompoundSubActions(parent).map((subAction, index) => (
      index === subIndex ? { ...subAction, [field]: value } : subAction
    ));
    setCompoundSubActions(stepKey, subActions);
  };

  const handleCompoundSubActionTypeChange = (stepKey: string, subIndex: number, newType: string, subAction: PwActionStep) => {
    const newFields = fieldsByType[newType] || [];
    const next: Record<string, unknown> = { type: newType };
    if (subAction.raw_action) next['raw_action'] = subAction.raw_action;
    newFields.forEach(f => {
      next[f] = ((subAction as any)[f] !== undefined && (subAction as any)[f] !== '') ? (subAction as any)[f] : '';
    });
    const parent = draft[stepKey] || {};
    const subActions = getCompoundSubActions(parent).map((current, index) => (
      index === subIndex ? next as PwActionStep : current
    ));
    setCompoundSubActions(stepKey, subActions);
  };

  const addCompoundSubAction = (stepKey: string) => {
    const parent = draft[stepKey] || {};
    setCompoundSubActions(stepKey, [...getCompoundSubActions(parent), makeBlankSubAction()]);
  };

  const deleteCompoundSubAction = (stepKey: string, subIndex: number) => {
    const parent = draft[stepKey] || {};
    setCompoundSubActions(stepKey, getCompoundSubActions(parent).filter((_, index) => index !== subIndex));
  };

  const renderCompoundSubActions = (stepKey: string, s: PwActionStep): React.ReactNode => {
    const subActions = getCompoundSubActions(s);
    const canEditSubActions = editing && canEditCurrent;
    const subActionTypes = ALL_ACTION_TYPES.filter(t => t !== 'CompoundAction');
    const compoundFieldStyle = { padding: '5px 10px', fontSize: '0.82rem', background: '#ffffff', border: '1.5px solid #fbbf24', borderRadius: 4, outline: 'none', transition: 'border-color 0.15s', width: '100%', boxSizing: 'border-box' as const };

    if (subActions.length === 0 && !canEditSubActions) {
      return (
        <div className="rrw-empty-panel" style={{ marginTop: 8, padding: '8px 10px' }}>
          No concrete sub-actions were stored for this compound action.
        </div>
      );
    }
    return (
      <div style={{ marginTop: 8, display: 'grid', gap: 6 }}>
        {subActions.length === 0 && (
          <div className="rrw-empty-panel" style={{ padding: '8px 10px' }}>
            No sub-actions yet.
          </div>
        )}
        {subActions.map((subAction, subIndex) => (
          <div
            key={`${subAction.type || 'sub-action'}-${subIndex}`}
            style={{
              padding: '8px 10px',
              border: '1px solid #dbeafe',
              borderRadius: 6,
              background: '#f8fbff',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 700, fontSize: '0.72rem', color: '#1d4ed8', textTransform: 'uppercase' }}>
                Sub-action {subIndex + 1}
              </span>
              {canEditSubActions ? (
                <select
                  style={{ ...compoundFieldStyle, width: 210, padding: '3px 8px', cursor: 'pointer' }}
                  value={subAction.type || 'ClickAction'}
                  onChange={e => handleCompoundSubActionTypeChange(stepKey, subIndex, e.target.value, subAction)}
                >
                  {subActionTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              ) : (
                <code style={{ fontSize: '0.72rem', background: '#e0ecff', color: '#1e40af', padding: '2px 7px', borderRadius: 4 }}>
                  {subAction.type || 'Action'}
                </code>
              )}
            </div>
            {canEditSubActions ? (
              <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: '6px 10px', marginTop: 8 }}>
                {(fieldsByType[subAction.type || 'ClickAction'] || ['locator_strategy', 'locator_value']).map(f => (
                  <React.Fragment key={f}>
                    <span style={{ color: '#92400e', fontSize: '0.78rem', fontWeight: 600, alignSelf: 'center' }}>{f}</span>
                    {f === 'locator_strategy' ? (
                      <select
                        style={{ ...compoundFieldStyle, cursor: 'pointer' }}
                        value={(subAction as any)[f] || ''}
                        onChange={e => updateCompoundSubActionField(stepKey, subIndex, f, e.target.value)}
                      >
                        <option value="">— select strategy —</option>
                        {LOCATOR_STRATEGIES.map(strategy => <option key={strategy} value={strategy}>{strategy}</option>)}
                      </select>
                    ) : f === 'wait_until' ? (
                      <select
                        style={{ ...compoundFieldStyle, cursor: 'pointer' }}
                        value={(subAction as any)[f] || 'domcontentloaded'}
                        onChange={e => updateCompoundSubActionField(stepKey, subIndex, f, e.target.value)}
                      >
                        <option value="domcontentloaded">domcontentloaded</option>
                        <option value="load">load</option>
                        <option value="networkidle">networkidle</option>
                      </select>
                    ) : f === 'should_exist' || f === 'submit_after' ? (
                      <select
                        style={{ ...compoundFieldStyle, cursor: 'pointer' }}
                        value={String((subAction as any)[f] ?? 'true')}
                        onChange={e => updateCompoundSubActionField(stepKey, subIndex, f, e.target.value)}
                      >
                        <option value="true">true</option>
                        <option value="false">false</option>
                      </select>
                    ) : (
                      <input
                        style={{ ...compoundFieldStyle, cursor: 'text' }}
                        value={(subAction as any)[f] || ''}
                        placeholder={`${f}...`}
                        onChange={e => updateCompoundSubActionField(stepKey, subIndex, f, e.target.value)}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>
            ) : (
              <>
                <div className="rrw-step-action" style={{ marginTop: 0 }}>{summarizeStep(subAction)}</div>
                {subAction.raw_action && (
                  <div className="rrw-step-expected" style={{ marginTop: 3, opacity: 0.8 }}>
                    {subAction.raw_action}
                  </div>
                )}
              </>
            )}
            {canEditSubActions && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
                <button
                  type="button"
                  title="Delete sub-action"
                  onClick={() => deleteCompoundSubAction(stepKey, subIndex)}
                  style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: 4, width: 22, height: 22, cursor: 'pointer', fontWeight: 700, fontSize: '1rem', lineHeight: 1, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >−</button>
              </div>
            )}
          </div>
        ))}
        {canEditSubActions && (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              type="button"
              title="Add sub-action"
              onClick={() => addCompoundSubAction(stepKey)}
              style={{ background: '#22c55e', color: '#fff', border: 'none', borderRadius: 4, width: 22, height: 22, cursor: 'pointer', fontWeight: 700, fontSize: '1rem', lineHeight: 1, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >+</button>
          </div>
        )}
      </div>
    );
  };

  /** Render error text with a "Read more / Read less" toggle beyond 300 chars. */
  const ErrorText = ({ text, id }: { text: string | null | undefined; id: string }) => {
    if (!text) return null;
    const LIMIT = 300;
    if (text.length <= LIMIT) return <>{text}</>;
    const expanded = expandedErrors[id] || false;
    return (
      <>
        {expanded ? text : text.slice(0, LIMIT) + '\u2026'}
        {' '}
        <span
          onClick={() => setExpandedErrors(prev => ({ ...prev, [id]: !expanded }))}
          style={{ color: '#7950f2', cursor: 'pointer', fontWeight: 600, fontSize: '0.72rem', textDecoration: 'underline' }}
        >
          {expanded ? 'Read less' : 'Read more'}
        </span>
      </>
    );
  };

  const AssertionDetails = ({ assertion, idPrefix }: { assertion?: StepResult; idPrefix: string }) => {
    const details = assertion?.assertion_details;
    if (!details) return null;
    const allRows: Array<[string, string | undefined]> = [
      ['Expected', details.expected],
      ['Actual', details.actual],
      ['Current URL', details.current_url],
      ['Page snapshot', details.page_snapshot],
      ['DOM snapshot', details.dom_snapshot],
    ];
    const rows = allRows.filter((row): row is [string, string] => Boolean(row[1]));

    if (!rows.length) return null;

    return (
      <div style={{ display: 'grid', gap: 6, marginTop: 6 }}>
        {rows.map(([label, value]) => (
          <div key={label} style={{ display: 'grid', gap: 2 }}>
            <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#5f3dc4', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
            <pre style={{ fontSize: '0.72rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: label === 'Actual' && assertion?.success === false ? '#b91c1c' : '#374151', background: label === 'Actual' && assertion?.success === false ? '#fff5f5' : '#f8f5ff', borderRadius: 4, padding: '6px 8px', margin: 0, border: label === 'Actual' && assertion?.success === false ? '1px solid #fca5a5' : '1px solid rgba(121, 80, 242, 0.18)' }}>
              <ErrorText text={String(value || '')} id={`${idPrefix}-${label.replace(/\s+/g, '-').toLowerCase()}`} />
            </pre>
          </div>
        ))}
      </div>
    );
  };

  return (
    <>
    <Modal show onHide={onClose} size="lg" scrollable centered className="tc-popup-modal">

      {/* ── Header (mirrors TestCasePopup) ── */}
      <Modal.Header className="tc-popup-header" closeButton>
        <div className="tc-popup-header-content">
          <div className="execution-popup-title-row">
            <div className="execution-popup-title-main">
              <div className="tc-popup-kicker">
                {test.test_case_key ? (
                  <a
                    href={`/projects/${runMeta.project_id}?tab=test-cases&testKey=${encodeURIComponent(test.test_case_key!)}`}
                    style={{ color: 'inherit', textDecoration: 'underline', cursor: 'pointer' }}
                    title={`Go to test case ${test.test_case_key}`}
                  >
                    {test.test_case_key}
                  </a>
                ) : (runMeta.run_name || 'Execution Result')}
              </div>
              <h5 className="tc-popup-title">{title}</h5>
            </div>
            {variants.length > 0 && (
              <div className="execution-variant-control">
                <span className="execution-variant-label">Variant</span>
                <select
                  className="rrw-filter-select execution-variant-select"
                  // The options come from `versionVariants`, so the value must be
                  // indexed against that same list. `activeVariantIdx` is clamped
                  // to the current-version list, which collapses to a single entry
                  // when only one variant was re-run — that made every selection
                  // in a previous version snap back to the first option.
                  value={selectedVersionVariantIdx}
                  onChange={(event) => setSelectedVariantIdx(Number(event.target.value))}
                  aria-label="Select data variant"
                >
                  {versionVariants.map((variant, index) => (
                    <option key={variant.execution_key || variant.variant_id || index} value={index}>
                      {formatVariantOption(variant, index)}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {historyEntries.length > 1 && (
              <div className="execution-variant-control">
                <span className="execution-variant-label">Version</span>
                <select
                  className="rrw-filter-select execution-variant-select"
                  value={selectedHistoryId}
                  onChange={(event) => {
                    setSelectedHistoryId(event.target.value);
                    setEditing(false);
                    setSaveError(null);
                    setRerunError(null);
                    setFetchedEvidence(null);
                    setEvidenceFetched(false);
                    setSelectedShotIdx(0);
                  }}
                  aria-label="Select variant version"
                >
                  {historyEntries.map(entry => (
                    <option key={entry.id} value={entry.id}>{entry.label}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div className="tc-popup-meta">
            <span className={`rrw-status-pill ${statusPillClass(versionResult)}`}>
              {statusLabel(versionResult)}
            </span>
            {runMeta.run_name && <span>{runMeta.run_name}</span>}
            {runMeta.base_url && <span>{runMeta.base_url}</span>}
            {runMeta.created_at && <span>{formatDatetime(runMeta.created_at)}</span>}
          </div>
        </div>
      </Modal.Header>

      {/* ── Tab action bar (mirrors TestCasePopup) ── */}
      <div className="tc-popup-action-bar">
        <div className="tc-popup-tabs">
          {TABS.map(tab => (
            <button
              key={tab.id}
              type="button"
              className={`tc-popup-tab ${activeTab === tab.id ? 'tc-popup-tab-active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Body ── */}
      <Modal.Body className="tc-popup-body">
        {!isHistorySelected && (
          <div className="rrw-summary-card mb-3">
            <Form.Group controlId="execution-auth-mode">
              <Form.Label>Starting session</Form.Label>
              <Form.Select value={authMode} disabled={authLoading || authSaving || rerunning}
                onChange={(event) => setAuthMode(event.target.value)}>
                <option value="">Not set (existing behavior)</option>
                <option value="authenticated">Authenticated</option>
                <option value="unauthenticated">Unauthenticated</option>
              </Form.Select>
              <Form.Text>Saved for this test case and used by future runs.</Form.Text>
            </Form.Group>
            <button type="button" className="tc-popup-tab mt-2" disabled={authLoading || authSaving || authMode === savedAuthMode}
              onClick={() => void saveAuthMode()}>{authSaving ? 'Saving...' : 'Save session setting'}</button>
            {authError && <div role="alert" className="text-danger mt-1">{authError}</div>}
          </div>
        )}
        {!isHistorySelected && activeActionUpdateLabel && (
          <div style={{
            marginBottom: 12,
            padding: '8px 12px',
            border: '1px solid #bbf7d0',
            borderRadius: 8,
            background: '#f0fdf4',
            color: '#166534',
            fontSize: '0.82rem',
            fontWeight: 600,
          }}>
            {activeActionUpdateLabel}
          </div>
        )}
        {isHistorySelected && (
          <div style={{
            marginBottom: 12,
            padding: '8px 12px',
            border: '1px solid #bfdbfe',
            borderRadius: 8,
            background: '#eff6ff',
            color: '#1e3a8a',
            fontSize: '0.82rem',
            fontWeight: 600,
          }}>
            Viewing previous version. Current run is not changed.
          </div>
        )}

        {/* ── Summary ── */}
        {activeTab === 'summary' && (
          <div className="rrw-detail-section">
            <div className="rrw-stat-grid">
              <div className="rrw-stat-card">
                <div className="rrw-stat-label">Result</div>
                <div className="rrw-stat-value">
                  <span className={`rrw-status-pill ${statusPillClass(versionResult)}`}>
                    {statusLabel(versionResult)}
                  </span>
                </div>
              </div>
              <div className="rrw-stat-card">
                <div className="rrw-stat-label">Duration</div>
                <div className="rrw-stat-value">{formatDuration(displayedResult.duration_ms)}</div>
              </div>
              <div className="rrw-stat-card">
                <div className="rrw-stat-label">Steps Passed</div>
                <div className="rrw-stat-value" style={{ color: '#0f766e' }}>{passedSteps}</div>
              </div>
              <div className="rrw-stat-card">
                <div className="rrw-stat-label">Steps Failed</div>
                <div className="rrw-stat-value" style={{ color: '#b91c1c' }}>{failedSteps}</div>
              </div>
            </div>

            <div className="rrw-summary-card">
              <div className="rrw-summary-title">Run Context</div>
              <div className="rrw-detail-meta" style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '6px 12px', fontSize: '0.85rem' }}>
                <span style={{ color: '#66788f', fontWeight: 600 }}>Run Name</span>
                <span>{runMeta.run_name || '—'}</span>
                <span style={{ color: '#66788f', fontWeight: 600 }}>Target URL</span>
                <span>
                  {runMeta.base_url
                    ? <a href={runMeta.base_url} target="_blank" rel="noopener noreferrer">{runMeta.base_url}</a>
                    : '—'}
                </span>
                <span style={{ color: '#66788f', fontWeight: 600 }}>Started</span>
                <span>{formatDatetime(runMeta.created_at)}</span>
                <span style={{ color: '#66788f', fontWeight: 600 }}>Test Key</span>
                <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{test.test_case_key || '—'}</span>
                {(displayedResult.variant_id || activeResult.variant_id) && (
                  <>
                    <span style={{ color: '#66788f', fontWeight: 600 }}>Variant</span>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{displayedResult.variant_id || activeResult.variant_id}</span>
                  </>
                )}
              </div>
            </div>

            {primaryScreenshot && (
              <div className="rrw-summary-card">
                <div className="rrw-summary-title">Final Screenshot</div>
                <div className="rrw-evidence-preview">
                  <img src={primaryScreenshot} alt="Final screenshot" className="rrw-evidence-image" style={{ maxHeight: 220, objectFit: 'contain' }} />
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'data' && (
          <div className="rrw-detail-section">
            <div className="rrw-summary-card">
              <div className="rrw-summary-title">Variant</div>
              <div className="rrw-detail-meta execution-data-meta-grid">
                <span className="execution-data-label">Variant ID</span>
                <span className="execution-data-value execution-data-value--mono">{displayedResult.variant_id || activeResult.variant_id || '—'}</span>
                <span className="execution-data-label">Dataset</span>
                <span className="execution-data-value">{displayedResult.dataset_name || displayedResult.dataset_id || activeResult.dataset_name || activeResult.dataset_id || '—'}</span>
                <span className="execution-data-label">Execution Key</span>
                <span className="execution-data-value execution-data-value--mono">{displayedResult.execution_key || activeResult.execution_key || '—'}</span>
              </div>
            </div>
            <div className="rrw-summary-card">
              <div className="rrw-summary-title">Fields</div>
              {displayedResult.data && Object.keys(displayedResult.data).length > 0 ? (
                <div className="execution-data-fields-grid">
                  {Object.entries(displayedResult.data).map(([key, value]) => (
                    <React.Fragment key={key}>
                      <span className="execution-data-label">{key}</span>
                      <span className="execution-data-value">{String(value ?? '')}</span>
                    </React.Fragment>
                  ))}
                </div>
              ) : (
                <div className="rrw-empty-panel">No runtime data was attached to this variant.</div>
              )}
            </div>
          </div>
        )}

        {/* ── Steps ── */}
        {activeTab === 'steps' && (
          <div className="rrw-detail-section">
            {stepsArray.length === 0 && displayedResult.pass === true && !displayedResult.error && (
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '10px 14px', marginBottom: 12, fontSize: '0.82rem', color: '#15803d' }}>
                This test was not re-executed in this run (it already passed). Execution steps are available from its last complete run.
              </div>
            )}
            {stepsArray.length === 0 && !(displayedResult.pass === true && !displayedResult.error) && (
              <div className="rrw-empty-panel">No step data recorded for this test.</div>
            )}
            {(() => {
              // Group each action step with the assertion that immediately follows it.
              type ActionGroup = { action: StepResult | string; assertion?: StepResult; origIdx: number };
              const groups: ActionGroup[] = [];
              let gi = 0;
              while (gi < stepsArray.length) {
                const s = stepsArray[gi];
                if (typeof s === 'string') {
                  groups.push({ action: s, origIdx: gi });
                  gi++;
                  continue;
                }
                const stepItem = s as StepResult;
                if (stepItem.is_assertion) { gi++; continue; } // orphaned assertion — skip
                const nextS = gi + 1 < stepsArray.length ? stepsArray[gi + 1] : null;
                const nextStep = nextS && typeof nextS === 'object' ? nextS as StepResult : null;
                if (nextStep?.is_assertion) {
                  groups.push({ action: stepItem, assertion: nextStep, origIdx: gi });
                  gi += 2;
                } else {
                  groups.push({ action: stepItem, origIdx: gi });
                  gi++;
                }
              }
              return groups.map(({ action: s, assertion, origIdx }, groupIdx) => {
                if (typeof s === 'string') {
                  return (
                    <div key={origIdx} className="rrw-step-card">
                      <div className="rrw-step-index">Step {groupIdx + 1}</div>
                      <div className="rrw-step-action">{s}</div>
                    </div>
                  );
                }
                const step = s as StepResult;
                const groupNotExecuted = !!step.not_executed;
                const groupPassed = !groupNotExecuted && step.success !== false && assertion?.success !== false;
                return (
                  <div
                    key={origIdx}
                    className="rrw-step-card"
                    style={{ borderLeft: `3px solid ${groupNotExecuted ? '#94a3b8' : groupPassed ? '#0f766e' : '#b91c1c'}` }}
                  >
                    <div className="rrw-step-index" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span>Step {groupIdx + 1}</span>
                      <span className={`rrw-status-pill ${groupNotExecuted ? '' : step.success ? 'rrw-status-pill-passed' : 'rrw-status-pill-failed'}`}
                            style={{ fontSize: '0.68rem', padding: '2px 8px', minHeight: 'auto' }}>
                        {groupNotExecuted ? 'Not executed' : step.success ? 'Pass' : 'Fail'}
                      </span>
                    </div>
                    <div className="rrw-step-action">{step.action || '—'}</div>
                    {step.expected && <div className="rrw-step-expected">Expected: {step.expected}</div>}
                    {step.message && <div className="rrw-step-expected" style={{ color: '#495057' }}>{step.message}</div>}
                    {step.error && (
                      <pre style={{ fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#b91c1c', background: '#fff5f5', borderRadius: 6, padding: '8px 10px', margin: '8px 0 0', border: '1px solid #fca5a5' }}>
                        <ErrorText text={step.error} id={`step-err-${origIdx}`} />
                      </pre>
                    )}
                    {assertion && (
                      <div style={{ marginTop: 8, borderLeft: '3px solid #7950f2', background: 'rgba(121, 80, 242, 0.04)', borderRadius: '0 4px 4px 0', padding: '7px 10px 7px 12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                          <span style={{ fontWeight: 700, fontSize: '0.72rem', color: '#7950f2', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Verify</span>
                          <span
                            className={`rrw-status-pill ${assertion.success ? 'rrw-status-pill-passed' : 'rrw-status-pill-failed'}`}
                            style={{ fontSize: '0.65rem', padding: '2px 7px', minHeight: 'auto', background: '#7950f2', color: '#fff' }}
                          >
                            {assertion.success ? '✓ Pass' : '✗ Fail'}
                          </span>
                        </div>
                        <div style={{ fontSize: '0.79rem', fontStyle: 'italic', color: '#5f3dc4' }}>{assertion.action || '—'}</div>
                        <AssertionDetails assertion={assertion} idPrefix={`assert-detail-${origIdx}`} />
                        {!assertion.assertion_details && assertion.message && <div style={{ fontSize: '0.77rem', color: '#495057', marginTop: 3 }}>{assertion.message}</div>}
                        {!assertion.assertion_details && assertion.error && (
                          <pre style={{ fontSize: '0.72rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#b91c1c', background: '#fff5f5', borderRadius: 4, padding: '6px 8px', margin: '4px 0 0', border: '1px solid #fca5a5' }}>
                            <ErrorText text={assertion.error} id={`assert-err-${origIdx}`} />
                          </pre>
                        )}
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        )}

        {/* ── Evidence (mirrors TestEvidencePanel) ── */}
        {activeTab === 'tc_steps' && (
          <div className="rrw-detail-section">
            {actionsLoading ? (
              <div className="rrw-loading" style={{ minHeight: 120 }}><Spinner animation="border" size="sm" /></div>
            ) : displayedTestCaseSteps && Object.keys(displayedTestCaseSteps).length > 0 ? (
              Object.entries(displayedTestCaseSteps)
                .sort(([a], [b]) => {
                  const na = parseInt(a.replace(/\D/g, ''), 10) || 0;
                  const nb = parseInt(b.replace(/\D/g, ''), 10) || 0;
                  return na - nb;
                })
                .map(([stepKey, step], i) => {
                  const stepNum = parseInt(stepKey.replace(/\D/g, ''), 10) || i + 1;
                  return (
                    <div key={stepKey} className="rrw-step-card" style={{ borderLeft: '3px solid #3b82f6' }}>
                      <div className="rrw-step-index" style={{ color: '#2563eb', fontWeight: 700 }}>Step {stepNum}</div>
                      <div className="rrw-step-action">{step.raw_action || '—'}</div>
                      {step.raw_expected && (
                        <div className="rrw-step-expected" style={{ marginTop: 6 }}>
                          <span style={{ fontWeight: 600, color: '#374151' }}>Expected: </span>
                          {step.raw_expected}
                        </div>
                      )}
                    </div>
                  );
                })
            ) : (
              <div className="rrw-empty-panel">No test case steps are available for this test.</div>
            )}
          </div>
        )}

        {/* ── Actions tab: editable Playwright actions for this test case ── */}
        {activeTab === 'actions' && (
          <div className="rrw-detail-section">
            <div style={{
              position: 'sticky',
              top: 0,
              zIndex: 2,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              marginBottom: 10,
              padding: '6px 10px',
              border: '1px solid #bfdbfe',
              borderRadius: 8,
              background: '#eff6ff',
              color: '#1e3a8a',
              fontSize: '0.78rem',
              fontWeight: 600,
            }}>
              {isHistorySelected
                ? 'Viewing previous version.'
                : `Edits apply only to ${variants.length > 0 ? `Variant ${activeVariantOrdinal}` : 'this test case'}.`}
            </div>
            {actionsLoading ? (
              <div className="rrw-loading" style={{ minHeight: 120 }}><Spinner animation="border" size="sm" /></div>
            ) : actionsError ? (
              <div className="rrw-empty-panel" style={{ background: '#fff5f5', color: '#b91c1c' }}>{actionsError}</div>
            ) : !displayedActions || Object.keys(displayedActions).length === 0 ? (
              <div className="rrw-empty-panel">
                No Playwright actions are cached for this test yet. Run the test at least once to populate them.
              </div>
            ) : (
              <div className="rrw-summary-card">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                  <div>
                    <div className="rrw-summary-title" style={{ marginBottom: 2 }}>Playwright Actions</div>
                    {!isHistorySelected && activeActionUpdateLabel && (
                      <div style={{ marginBottom: 6, fontSize: '0.82rem', color: '#166534', fontWeight: 600 }}>
                        {activeActionUpdateLabel}
                      </div>
                    )}
                    <div style={{ fontSize: '0.78rem', color: '#66788f' }}>
                      {Object.keys(displayedActions).length} step{Object.keys(displayedActions).length !== 1 ? 's' : ''} · {isHistorySelected ? 'history is read-only' : `edits apply only to ${variants.length > 0 ? `Variant ${activeVariantOrdinal}` : 'this test case'}`}.
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {!editing ? (
                      canEditCurrent && (
                      <button type="button" className="tc-popup-tab" onClick={() => setEditing(true)}>Edit</button>
                      )
                    ) : (
                      <>
                        <button type="button" className="tc-popup-tab" onClick={cancelEdit} disabled={saving || rerunning}>Cancel</button>
                        <button
                          type="button"
                          className="tc-popup-tab tc-popup-tab-active"
                          onClick={() => void saveActions()}
                          disabled={saving || rerunning}
                        >
                          {saving ? <Spinner animation="border" size="sm" /> : 'Save'}
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      className="tc-popup-tab tc-popup-tab-active"
                      onClick={() => void rerunSingleTest()}
                      disabled={rerunning || saving || isHistorySelected || testExecutionActionDisabled}
                      title={variants.length > 0 ? `Save and re-run Variant ${activeVariantOrdinal}` : 'Save and re-run only this test case'}
                    >
                      {rerunning ? <Spinner animation="border" size="sm" /> : (editing ? 'Save & Re-run' : (variants.length > 0 ? `Re-run Variant ${activeVariantOrdinal}` : 'Re-run This Test'))}
                    </button>
                  </div>
                </div>
                {saveError && (
                  <div className="rrw-empty-panel" style={{ background: '#fff5f5', color: '#b91c1c', marginBottom: 8 }}>{saveError}</div>
                )}
                {rerunError && (
                  <div className="rrw-empty-panel" style={{ background: '#fff5f5', color: '#b91c1c', marginBottom: 8 }}>{rerunError}</div>
                )}

                <div className="rrw-history-list">
                  {(() => {
                    // Each action step is rendered with its assertion nested inside as a sub-step.
                    type FlatAction = { kind: 'action'; cacheKey: string; step: PwActionStep };
                    type FlatItem = FlatAction;

                    const flatItems: FlatItem[] = [];
                    Object.entries(displayedActions)
                      .sort(([a], [b]) =>
                        (parseInt(a.replace(/\D/g, ''), 10) || 0) - (parseInt(b.replace(/\D/g, ''), 10) || 0)
                      )
                      .forEach(([cacheKey, step]) => {
                        flatItems.push({ kind: 'action', cacheKey, step });
                      });

                    return flatItems.map((item, displayIdx) => {

                      /* ── Action step (assertion is nested inside as a sub-step) ── */
                      const { cacheKey: stepKey, step } = item;
                      const displayNum = parseInt(stepKey.replace(/\D/g, ''), 10) || displayIdx + 1;
                      const actionType = step.type || '';
                      const isCompoundAction = actionType === 'CompoundAction';
                      const fields = isCompoundAction ? [] : (fieldsByType[actionType] || ['url', 'locator_strategy', 'locator_value', 'value']);
                      const updateSummary = actionUpdateByStepKey[stepKey];
                      const fieldStyle = { padding: '5px 10px', fontSize: '0.82rem', background: '#ffffff', border: '1.5px solid #fbbf24', borderRadius: 4, outline: 'none', transition: 'border-color 0.15s', width: '100%', boxSizing: 'border-box' as const, cursor: 'pointer' };
                      return (
                      <div key={stepKey} className="rrw-step-card" style={{ borderLeft: '3px solid #2563eb' }}>
                        <div className="rrw-step-index" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 700 }}>Step {displayNum}</span>
                          <span style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#64748b' }}>{stepKey}</span>
                          {editing && canEditCurrent ? (() => {
                            const isOpen = !!fuzzyOpen[stepKey];
                            const query = fuzzyQuery[stepKey] ?? actionType;
                            const filtered = ALL_ACTION_TYPES.filter(t =>
                              t.toLowerCase().includes(query.toLowerCase())
                            );
                            return (
                              <div style={{ position: 'relative' }}>
                                <input
                                  style={{ width: 210, padding: '2px 8px', fontSize: '0.78rem', background: '#fffbeb', border: '1.5px solid #f59e0b', borderRadius: 4, outline: 'none' }}
                                  value={query}
                                  placeholder="Type to search action..."
                                  autoComplete="off"
                                  onChange={e => {
                                    setFuzzyQuery(prev => ({ ...prev, [stepKey]: e.target.value }));
                                    setFuzzyOpen(prev => ({ ...prev, [stepKey]: true }));
                                  }}
                                  onFocus={() => setFuzzyOpen(prev => ({ ...prev, [stepKey]: true }))}
                                  onBlur={() => setTimeout(() => setFuzzyOpen(prev => ({ ...prev, [stepKey]: false })), 150)}
                                />
                                {isOpen && filtered.length > 0 && (
                                  <div style={{
                                    position: 'absolute', top: '100%', left: 0, zIndex: 9999,
                                    background: '#fff', border: '1px solid #d1d5db', borderRadius: 6,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)', minWidth: 220,
                                    maxHeight: 220, overflowY: 'auto', marginTop: 2,
                                  }}>
                                    {filtered.map(t => (
                                      <div
                                        key={t}
                                        onMouseDown={() => {
                                          handleActionTypeChange(stepKey, t, step);
                                          setFuzzyQuery(prev => ({ ...prev, [stepKey]: t }));
                                          setFuzzyOpen(prev => ({ ...prev, [stepKey]: false }));
                                        }}
                                        style={{
                                          padding: '6px 12px', fontSize: '0.82rem', cursor: 'pointer',
                                          background: t === actionType ? '#fffbeb' : 'transparent',
                                          color: ACTION_TYPES_ADVANCED.includes(t as any) ? '#92400e' : '#1e293b',
                                          borderBottom: '1px solid #f1f5f9',
                                        }}
                                        onMouseEnter={e => (e.currentTarget.style.background = '#fef9c3')}
                                        onMouseLeave={e => (e.currentTarget.style.background = t === actionType ? '#fffbeb' : 'transparent')}
                                      >
                                        {t}{ACTION_TYPES_ADVANCED.includes(t as any) ? ' (advanced)' : ''}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })() : (
                            <code style={{ fontSize: '0.78rem', background: '#eef2ff', color: '#3730a3', padding: '2px 8px', borderRadius: 4 }}>
                              {actionType || '—'}
                            </code>
                          )}
                          {updateSummary && (
                            <span className="execution-action-update-pill">
                              {updateSummary.label || `Step ${updateSummary.step_number} action updated in v${updateSummary.version_number}`}
                            </span>
                          )}
                        </div>
                        {editing && canEditCurrent ? (
                          isCompoundAction ? (
                            <div style={{ marginTop: 8, padding: '10px 12px', background: '#fffbeb', border: '1.5px solid #f59e0b', borderRadius: 6 }}>
                              <div style={{ color: '#92400e', fontSize: '0.78rem', fontWeight: 600, marginBottom: 8 }}>
                                Compound action sub-actions
                              </div>
                              {renderCompoundSubActions(stepKey, step)}
                            </div>
                          ) : (
                          <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: '6px 10px', marginTop: 8, padding: '10px 12px', background: '#fffbeb', border: '1.5px solid #f59e0b', borderRadius: 6 }}>
                              {fields.map(f => (
                                <React.Fragment key={f}>
                                  <span style={{ color: '#92400e', fontSize: '0.78rem', fontWeight: 600, alignSelf: 'center' }}>{f}</span>
                                  {f === 'locator_strategy' ? (
                                    <select style={fieldStyle}
                                      value={(step as any)[f] || ''}
                                      onChange={e => updateDraftField(stepKey, f, e.target.value)}
                                    >
                                      <option value="">— select strategy —</option>
                                      {LOCATOR_STRATEGIES.map(s => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                  ) : f === 'wait_until' ? (
                                    <select style={fieldStyle}
                                      value={(step as any)[f] || 'domcontentloaded'}
                                      onChange={e => updateDraftField(stepKey, f, e.target.value)}
                                    >
                                      <option value="domcontentloaded">domcontentloaded</option>
                                      <option value="load">load</option>
                                      <option value="networkidle">networkidle</option>
                                    </select>
                                  ) : f === 'should_exist' || f === 'submit_after' ? (
                                    <select style={fieldStyle}
                                      value={String((step as any)[f] ?? 'true')}
                                      onChange={e => updateDraftField(stepKey, f, e.target.value)}
                                    >
                                      <option value="true">true</option>
                                      <option value="false">false</option>
                                    </select>
                                  ) : (
                                    <input
                                      style={{ ...fieldStyle, cursor: 'text' }}
                                      value={(step as any)[f] || ''}
                                      placeholder={`${f}…`}
                                      onChange={e => updateDraftField(stepKey, f, e.target.value)}
                                      onFocus={e => { e.target.style.borderColor = '#d97706'; e.target.style.boxShadow = '0 0 0 2px #fde68a'; }}
                                      onBlur={e => { e.target.style.borderColor = '#fbbf24'; e.target.style.boxShadow = 'none'; }}
                                    />
                                  )}
                                </React.Fragment>
                              ))}
                          </div>
                          )
                        ) : (
                          <>
                            {isCompoundAction ? renderCompoundSubActions(stepKey, step) : (
                              <div className="rrw-step-action">{summarizeStep(step)}</div>
                            )}
                            {step.raw_action && (
                              <div className="rrw-step-expected" style={{ marginTop: 4, opacity: 0.8 }}>
                                {step.raw_action}
                              </div>
                            )}
                            {/* Assertion sub-step: expected outcome + live result */}
                            {(step.raw_expected || assertionByStepKey[stepKey]) && (() => {
                              // A saved execution result only verifies the expected text that
                              // existed when that run was executed. Do not label it Pass/Fail
                              // for a newer authored Verify statement.
                              const assertionMatchesCurrentVersion = isHistorySelected || hasMatchingCurrentAssertion(
                                currentRunActions,
                                testCaseSteps,
                                stepKey,
                              );
                              const recordedAssertion = assertionByStepKey[stepKey];
                              const asr = assertionMatchesCurrentVersion ? recordedAssertion : undefined;
                              return (
                                <div style={{ marginTop: 8, borderLeft: '3px solid #7950f2', background: 'rgba(121, 80, 242, 0.04)', borderRadius: '0 4px 4px 0', padding: '7px 10px 7px 12px' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                                    <span style={{ fontWeight: 700, fontSize: '0.72rem', color: '#7950f2', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Verify</span>
                                    {asr && (
                                      <span
                                        className={`rrw-status-pill ${asr.success ? 'rrw-status-pill-passed' : 'rrw-status-pill-failed'}`}
                                        style={{ fontSize: '0.65rem', padding: '2px 7px', minHeight: 'auto', background: '#7950f2', color: '#fff' }}
                                      >
                                        {asr.success ? '✓ Pass' : '✗ Fail'}
                                      </span>
                                    )}
                                    {!asr && recordedAssertion && !isHistorySelected && (
                                      <span style={{ fontSize: '0.65rem', padding: '2px 7px', background: '#64748b', color: '#fff', borderRadius: 999 }}>
                                        Not run for this version
                                      </span>
                                    )}
                                  </div>
                                  {step.raw_expected && (
                                    <div style={{ fontSize: '0.8rem', fontStyle: 'italic', color: '#5f3dc4', marginBottom: asr ? 4 : 0 }}>
                                      {step.raw_expected}
                                    </div>
                                  )}
                                  <AssertionDetails assertion={asr} idPrefix={`script-assert-detail-${stepKey}`} />
                                  {!asr?.assertion_details && asr?.message && (
                                    <div style={{ fontSize: '0.77rem', color: '#495057' }}>{asr.message}</div>
                                  )}
                                  {!asr?.assertion_details && asr?.error && (
                                    <pre style={{ fontSize: '0.72rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#b91c1c', background: '#fff5f5', borderRadius: 4, padding: '6px 8px', margin: '4px 0 0', border: '1px solid #fca5a5' }}>
                                      <ErrorText text={asr.error} id={`script-assert-err-${stepKey}`} />
                                    </pre>
                                  )}
                                </div>
                              );
                            })()}
                          </>
                        )}
                        {/* Verify sub-step edit row — always shown in edit mode */}
                        {editing && (
                          <div style={{ marginTop: 8, padding: '8px 10px', background: 'rgba(121, 80, 242, 0.06)', border: '1.5px solid #7950f2', borderRadius: 6 }}>
                            <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr', gap: '6px 10px' }}>
                              <span style={{ color: '#5f3dc4', fontSize: '0.78rem', fontWeight: 600, alignSelf: 'center' }}>verify (expected)</span>
                              <input
                                style={{ padding: '5px 10px', fontSize: '0.82rem', background: '#fff', border: '1.5px solid #7950f2', borderRadius: 4, outline: 'none', width: '100%', boxSizing: 'border-box' as const }}
                                value={step.raw_expected || ''}
                                placeholder="What should happen after this step…"
                                onChange={e => updateDraftField(stepKey, 'raw_expected', e.target.value)}
                                onFocus={e => { e.target.style.borderColor = '#5f3dc4'; e.target.style.boxShadow = '0 0 0 2px rgba(121,80,242,0.2)'; }}
                                onBlur={e => { e.target.style.borderColor = '#7950f2'; e.target.style.boxShadow = 'none'; }}
                              />
                            </div>
                          </div>
                        )}
                          {editing && (
                            <div style={{ display: 'flex', gap: 4, marginTop: 8, justifyContent: 'flex-end' }}>
                              <button
                                type="button"
                                title="Insert step after this one"
                                onClick={() => insertStepAfter(stepKey)}
                                style={{ background: '#22c55e', color: '#fff', border: 'none', borderRadius: 4, width: 22, height: 22, cursor: 'pointer', fontWeight: 700, fontSize: '1rem', lineHeight: 1, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                              >+</button>
                              <button
                                type="button"
                                title="Delete this step"
                                onClick={() => deleteStep(stepKey)}
                                disabled={Object.keys(displayedActions).length <= 1}
                                style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: 4, width: 22, height: 22, cursor: 'pointer', fontWeight: 700, fontSize: '1rem', lineHeight: 1, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: Object.keys(displayedActions).length <= 1 ? 0.4 : 1 }}
                              >−</button>
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Evidence (mirrors TestEvidencePanel) ── */}
        {activeTab === 'evidence' && (
          <div className="rrw-detail-section">
            {evidenceLoading ? (
              <div className="rrw-loading" style={{ minHeight: 120, display: 'grid', placeItems: 'center', gap: 8 }}>
                <Spinner animation="border" size="sm" />
                <div style={{ fontSize: '0.84rem', color: '#475569' }}>Loading evidence for the selected variant...</div>
              </div>
            ) : !fetchedEvidence || (fetchedEvidence.files ?? []).length === 0 ? (
              <div className="rrw-empty-panel">
                No screenshots, traces, or result files are available for the selected run.
              </div>
            ) : (() => {
              const stepShots = (fetchedEvidence.files ?? [])
                .filter(f => f.kind === 'image' && /^step_\d+\.webp$/i.test(f.name))
                .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }));
              // Build unified slides: video first, then step screenshots
              type Slide = { label: string; url: string; kind: 'image' | 'video'; downloadUrl: string };
              const videoFiles = (fetchedEvidence.files ?? []).filter(f =>
                !!f.download_url && (f.kind === 'video' || f.name.toLowerCase().endsWith('.webm') || f.name.toLowerCase().endsWith('.mp4'))
              );
              const videoSlides: Slide[] = videoFiles.map(f => ({
                label: 'Test Recording',
                url: f.download_url!,
                kind: 'video' as const,
                downloadUrl: f.download_url!,
              }));
              const imageSlides: Slide[] = stepShots.map(f => ({
                label: `Step ${parseInt(f.name.replace(/[^0-9]/g, ''), 10)}`,
                url: f.download_url ?? '',
                kind: 'image' as const,
                downloadUrl: f.download_url ?? '',
              })).filter(s => s.url);
              const allSlides: Slide[] = [...videoSlides, ...imageSlides];
              const clamp = (n: number) => Math.max(0, Math.min(n, allSlides.length - 1));
              const idx = clamp(selectedShotIdx);
              const current = allSlides[idx];
              // Other downloadable files (non-screenshot, non-video)
              const otherFiles = (fetchedEvidence.files ?? []).filter(f => {
                if (!f.download_url) return false;
                if (f.name.toLowerCase() === 'final_screenshot.webp' || f.name.toLowerCase() === 'final_screenshot.png') return false;
                if (f.kind === 'image' && /^step_\d+\.webp$/i.test(f.name)) return false;
                if (f.kind === 'video' || f.name.toLowerCase().endsWith('.webm') || f.name.toLowerCase().endsWith('.mp4')) return false;
                return true;
              });
              return (
                <div className="rrw-evidence">
                  {/* ── Unified carousel (video slide first, then screenshots) ── */}
                  {allSlides.length > 0 && current && (
                    <div style={{ position: 'relative', background: current.kind === 'video' ? '#000' : '#f1f5f9', borderRadius: 10, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                      {/* Header bar */}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 14px', background: current.kind === 'video' ? '#1e293b' : 'rgba(255,255,255,0.8)', borderBottom: '1px solid #e2e8f0' }}>
                        <span style={{ fontSize: '0.8rem', fontWeight: 600, color: current.kind === 'video' ? '#e2e8f0' : '#374151' }}>
                          {current.kind === 'video' ? '🎥 ' : ''}{current.label}
                        </span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          {current.kind === 'video' && (
                            <a href={current.downloadUrl} download style={{ fontSize: '0.72rem', color: '#94a3b8', textDecoration: 'none' }}>download</a>
                          )}
                          <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{idx + 1} / {allSlides.length}</span>
                        </div>
                      </div>
                      {/* Slide content */}
                      {current.kind === 'video' ? (
                        <video src={current.url} controls style={{ width: '100%', display: 'block', maxHeight: 380 }} />
                      ) : (
                        <img
                          src={current.url}
                          alt={current.label}
                          style={{ width: '100%', maxHeight: 380, objectFit: 'contain', display: 'block', background: '#f8fafc' }}
                        />
                      )}
                      {/* Prev / Next buttons */}
                      {allSlides.length > 1 && (
                        <>
                          <button
                            onClick={() => setSelectedShotIdx(clamp(idx - 1))}
                            disabled={idx === 0}
                            style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.9)', border: '1px solid #d1d5db', borderRadius: 6, width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: idx === 0 ? 0.35 : 1 }}
                            aria-label="Previous"
                          >‹</button>
                          <button
                            onClick={() => setSelectedShotIdx(clamp(idx + 1))}
                            disabled={idx === allSlides.length - 1}
                            style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.9)', border: '1px solid #d1d5db', borderRadius: 6, width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: idx === allSlides.length - 1 ? 0.35 : 1 }}
                            aria-label="Next"
                          >›</button>
                        </>
                      )}
                    </div>
                  )}
                  {/* ── Thumbnail strip ── */}
                  {allSlides.length > 1 && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
                      {allSlides.map((slide, i) => (
                        <button
                          key={i}
                          onClick={() => setSelectedShotIdx(i)}
                          style={{ padding: 0, border: i === idx ? '2px solid #3b82f6' : '2px solid transparent', borderRadius: 7, background: 'none', cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, outline: 'none' }}
                          aria-label={slide.label}
                        >
                          {slide.kind === 'video' ? (
                            <div style={{ width: 100, height: 66, borderRadius: 5, background: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.6rem' }}>🎥</div>
                          ) : (
                            <img src={slide.url} alt={slide.label} style={{ width: 100, height: 66, objectFit: 'cover', borderRadius: 5, background: '#f1f5f9', display: 'block' }} />
                          )}
                          <span style={{ fontSize: '0.68rem', color: i === idx ? '#2563eb' : '#6b7280', fontWeight: i === idx ? 600 : 400 }}>{slide.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {/* ── Other files ── */}
                  {otherFiles.length > 0 && (
                    <div className="rrw-evidence-files" style={{ marginTop: 14 }}>
                      {otherFiles.map((f, i) => {
                        const isTrace = f.kind === 'trace' || f.name.toLowerCase().endsWith('.zip');
                        return isTrace ? (
                          <a
                            key={i}
                            className="rrw-evidence-file"
                            href={f.download_url}
                            download={f.name}
                            rel="noreferrer"
                            style={{ borderColor: '#f59e0b', background: '#fffbeb', color: '#92400e', gap: 8 }}
                            title="Download Playwright trace — open with https://trace.playwright.dev"
                          >
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 }}>
                              <span style={{ fontSize: '1.1rem' }}>🎬</span>
                              {f.name}
                            </span>
                            <span style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#b45309', background: '#fef3c7', borderRadius: 6, padding: '2px 7px', border: '1px solid #fcd34d', whiteSpace: 'nowrap' }}>
                              download
                            </span>
                          </a>
                        ) : (
                          <a key={i} className="rrw-evidence-file" href={f.download_url} target="_blank" rel="noreferrer">
                            <span>{f.name}</span>
                            <span className="rrw-evidence-kind">{f.kind}</span>
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* ── Execution Log ── */}
        {activeTab === 'execution' && (
          <div className="rrw-detail-section">
            <div className="rrw-summary-card">
              <div className="rrw-summary-title">Execution Result</div>
              <div className="rrw-detail-meta">
                <span className={`rrw-status-pill ${statusPillClass(displayedResult)}`}>{statusLabel(displayedResult)}</span>
                <span>{formatDuration(displayedResult.duration_ms)}</span>
              </div>
              {displayedResult.error ? (
                <pre style={{ fontSize: '0.78rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#b91c1c', background: '#fff5f5', borderRadius: 8, padding: '10px 12px', margin: '12px 0 0', border: '1px solid #fca5a5' }}>
                  {displayedResult.error}
                </pre>
              ) : (
                <p className="rrw-summary-copy">No top-level error was recorded for this test.</p>
              )}
            </div>

            {stepsArray.some(s => typeof s === 'object' && (s as StepResult).error) && (
              <div className="rrw-summary-card">
                <div className="rrw-summary-title">Step Errors</div>
                {stepsArray.map((s, i) => {
                  if (typeof s === 'string' || !(s as StepResult).error) return null;
                  const step = s as StepResult;
                  return (
                    <div key={i} className="rrw-history-row" style={{ flexDirection: 'column', alignItems: 'flex-start', gap: 6, marginBottom: 8 }}>
                      <span className="rrw-history-title">Step {step.step_index ?? i + 1} — {step.action}</span>
                      <pre style={{ fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#b91c1c', background: '#fff5f5', borderRadius: 6, padding: '6px 8px', margin: 0, width: '100%', border: '1px solid #fca5a5' }}>
                        <ErrorText text={step.error} id={`summary-err-${i}`} />
                      </pre>
                    </div>
                  );
                })}
              </div>
            )}

            {!displayedResult.error && stepsArray.every(s => typeof s !== 'object' || (s as StepResult).success !== false) && (
              <div className="rrw-empty-panel" style={{ background: '#d1fae5', color: '#0f766e' }}>
                All steps executed successfully — no errors logged.
              </div>
            )}
          </div>
        )}

        {/* ── Timing ── */}
        {activeTab === 'timing' && (
          <div className="rrw-detail-section">
            <div className="rrw-summary-card">
              <div className="rrw-summary-title">Execution Timeline</div>
              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: '6px 12px', fontSize: '0.85rem' }}>
                <span style={{ color: '#66788f', fontWeight: 600 }}>Start Time</span>
                <span>{formatDatetime(displayedResult.start_time)}</span>
                <span style={{ color: '#66788f', fontWeight: 600 }}>End Time</span>
                <span>{formatDatetime(displayedResult.end_time)}</span>
                <span style={{ color: '#66788f', fontWeight: 600 }}>Duration</span>
                <span>{formatDuration(displayedResult.duration_ms)}</span>
              </div>
            </div>

            {stepsArray.length > 0 && (
              <div className="rrw-summary-card">
                <div className="rrw-summary-title">Step Breakdown</div>
                <div className="rrw-history-list">
                  {(() => {
                    const durations = stepsArray
                      .filter(s => typeof s === 'object' && (s as StepResult).duration_ms != null)
                      .map(s => (s as StepResult).duration_ms as number);
                    const maxDuration = durations.length > 0 ? Math.max(...durations) : 0;
                    return stepsArray.map((s, i) => {
                      if (typeof s === 'string') return (
                        <div key={i} className="rrw-history-row">
                          <span className="rrw-history-title">Step {i + 1}</span>
                          <span className="rrw-history-subtitle">{s}</span>
                        </div>
                      );
                      const step = s as StepResult;
                      const pct = maxDuration > 0 && step.duration_ms != null
                        ? Math.max(4, Math.round((step.duration_ms / maxDuration) * 100))
                        : null;
                      return (
                        <div key={i} className="rrw-history-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 4 }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div>
                              <div className="rrw-history-title">Step {step.step_index ?? i + 1}</div>
                              <div className="rrw-history-subtitle">{step.action || `Step ${i + 1}`}</div>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                              {step.duration_ms != null && (
                                <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', fontFamily: 'monospace' }}>
                                  {formatDuration(step.duration_ms)}
                                </span>
                              )}
                              <span className={`rrw-status-pill ${step.success ? 'rrw-status-pill-passed' : 'rrw-status-pill-failed'}`}
                                    style={{ fontSize: '0.7rem', padding: '2px 8px', minHeight: 'auto' }}>
                                {step.success ? 'Pass' : 'Fail'}
                              </span>
                            </div>
                          </div>
                          {pct != null && (
                            <div style={{ height: 5, borderRadius: 3, background: '#e5e7eb', overflow: 'hidden' }}>
                              <div style={{
                                width: `${pct}%`,
                                height: '100%',
                                borderRadius: 3,
                                background: step.success ? '#0f766e' : '#b91c1c',
                              }} />
                            </div>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Failure Analysis Chat ── */}
        {activeTab === 'chat' && (
          <div className="rrw-detail-section">
            {runMeta.project_id && failureChatContext ? (
              <ProjectChatPanel
                key={`${runMeta.job_id || 'job'}:${runMeta.run_id || 'run'}:${failureChatContext.test_key || 'test'}:${failureChatContext.variant_id || 'base'}`}
                projectId={runMeta.project_id}
                variant="embedded"
                title="Failure Analysis Chat"
                subtitle={runMeta.run_id ? 'Scoped to this Playwright run and selected test.' : 'Scoped to this Playwright job and selected test.'}
                context={failureChatContext}
                allowMaximize
              />
            ) : (
              <div className="rrw-empty-panel">Chat context is not available for this execution preview.</div>
            )}
          </div>
        )}

      </Modal.Body>
    </Modal>
    </>
  );
}
